import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Divider,
  Card,
  CardContent,
  Chip,
  IconButton,
  Grid,
  OutlinedInput,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Psychology as PsychologyIcon,
  Timer as TimerIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { testService } from '../services/testService';
import evaluationService from '../services/evaluationService';
import { TestFormData, TEST_TYPE_OPTIONS, TEST_CONFIGS, formatTime, isScoreBasedTest, getTestResultMode, calculateStroopInterference, calculateHVLTRTotalRecall, BNT_WORD_LIST, getPhoneticKeyParts } from '../types/test';
import HVLTRInput from './HVLTRInput';
import { LoadingOrb } from './LoadingOrb';
import MocaDetailedView from './MocaDetailedView';

/** Puntuaciones válidas por elemento (Figura de Rey) */
const REY_SCORES = [0, 0.5, 1, 2] as const;

/** Nombres de los 18 elementos de la Figura Compleja de Rey */
const REY_ELEMENTOS = [
  'Cruz exterior, ángulo superior izquierdo',
  'Rectángulo grande, armazón de la figura',
  'Cruz de San Andrés, diagonal del rectángulo grande',
  'Mediana horizontal del rectángulo grande 2',
  'Mediana vertical del rectángulo grande 2',
  'Rectángulo pequeño en rectángulo grande',
  'Segmento pequeño sobre el rectángulo 6',
  '4 líneas paralelas en triángulo superior izquierdo',
  'Triángulo rectángulo sobre rectángulo grande (a la derecha)',
  'Pequeña perpendicular en cuadrante superior derecha',
  'Círculo con tres puntos en cuadrante superior derecho',
  '5 líneas paralelas en cuadrante inferior derecha',
  'Dos lados externos del triángulo isósceles de la derecha',
  'Pequeño rombo en vértice extremo del triángulo 13',
  'Segmento vertical en el interior del triángulo 13',
  'Prolongación de la mediana horizontal, altura de triángulo 13',
  'Cruz en extremo inferior del rectángulo 2',
  'Cuadrado y diagonal en extremo inferior izquierdo'
];

const REY_COLS = ['copia', 'memoriaInmediata', 'memoriaDiferida'] as const;
type ReyCol = typeof REY_COLS[number];
type ElevatorVersionKey = 'versionA' | 'versionB';

const ELEVATOR_SUBTEST_A = [
  { tone: 'A', values: [3, 5, 6, 8, 11, 9, 14] },
];

const ELEVATOR_SUBTEST_B = [
  { tone: 'A', values: [2, 4, 6, 8, 7, 10, 9, 12, 11, 14] },
];

interface AddTestModalProps {
  open: boolean;
  onClose: () => void;
  patientId: number;
  patientName: string;
  patientEducationYears?: number;
  patientDateOfBirth?: string;
  onTestAdded?: (test: any) => void;
}

const AddTestModal: React.FC<AddTestModalProps> = ({
  open,
  onClose,
  patientId,
  patientName,
  patientEducationYears,
  patientDateOfBirth,
  onTestAdded
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Matriz de clasificación a usar para esta evaluación: 'original' | 'normal' | 'non-normal'
  const [matrixType, setMatrixType] = useState<'original' | 'normal' | 'non-normal'>('original');
  const [formData, setFormData] = useState<TestFormData>({
    testTypeCode: 'TMT-A',
    patientId,
    testData: {
      timeSeconds: undefined,
      errors: undefined,
      // Campos para Stroop
      palabraScore: 0,
      colorScore: 0,
      palabraColorScore: 0,
      interferenciaScore: 0,
      // Campos para Fluencias
      animalsScore: undefined,
      letterFScore: undefined,
      letterAScore: undefined,
      letterSScore: undefined,
      // Campos para HVLT-R
      ensayo1Score: 0,
      ensayo2Score: 0,
      ensayo3Score: 0,
      ensayo4Score: 0,
      recuerdoTotalScore: 0,
      recuerdoTardioScore: 0,
      // Campos para Rey Figure
      copiaExactitud: undefined,
      copiaTiempo: undefined,
      copiaTipo: undefined,
      memoriaExactitud: undefined,
      // Campos para D2
      trScore: undefined,
      taScore: undefined,
      oScore: undefined,
      cScore: undefined,
      trPlusScore: undefined,
      trMinusScore: undefined,
      varScore: undefined,
      totScore: undefined,
      errorRate: undefined,
      concentrationScore: undefined
    },
    observations: ''
  });
  // Estados para las palabras de cada intento en HVLT-R
  const [hvltRWords, setHvltRWords] = useState<{
    ensayo1: string[];
    ensayo2: string[];
    ensayo3: string[];
    ensayo4: string[];
  }>({
    ensayo1: [],
    ensayo2: [],
    ensayo3: [],
    ensayo4: []
  });
  const [calculatedPercentile, setCalculatedPercentile] = useState<{
    percentile: number | string | null;
    isRange: boolean;
    classifications: string[];
  } | null>(null);
  const [calculatingPercentile, setCalculatingPercentile] = useState(false);
  
  // Para Stroop: percentiles por condición
  const [stroopPercentiles, setStroopPercentiles] = useState<{
    palabra?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    color?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    palabraColor?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
  }>({});
  const [calculatingStroopPercentile, setCalculatingStroopPercentile] = useState<string | null>(null);
  
  // Para Fluencias: percentiles por categoría
  const [fluenciasPercentiles, setFluenciasPercentiles] = useState<{
    animals?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    letterF?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    letterA?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    letterS?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
  }>({});
  const [calculatingFluenciasPercentile, setCalculatingFluenciasPercentile] = useState<string | null>(null);
  
  // Para HVLT-R: percentiles por condición
  const [hvltRPercentiles, setHvltRPercentiles] = useState<{
    recuerdoTotal?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    recuerdoTardio?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
  }>({});
  const [calculatingHVLTRPercentile, setCalculatingHVLTRPercentile] = useState<string | null>(null);
  
  // Para D2: percentiles por métrica
  const [d2Percentiles, setD2Percentiles] = useState<{
    TR?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    TA?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    O?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    C?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    TOT?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    'TR+'?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    'TR-'?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    VAR?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    CON?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
  }>({});
  const [calculatingD2Percentile, setCalculatingD2Percentile] = useState<string | null>(null);
  const [d2Conversions, setD2Conversions] = useState<Record<string, any[]>>({});
  
  // Para Rey Figure: estados
  const [reyFigureCopiaImage, setReyFigureCopiaImage] = useState<File | null>(null);
  const [reyFigureCopiaImagePreview, setReyFigureCopiaImagePreview] = useState<string | null>(null);
  const [reyFigureMemoriaInmediataImage, setReyFigureMemoriaInmediataImage] = useState<File | null>(null);
  const [reyFigureMemoriaInmediataImagePreview, setReyFigureMemoriaInmediataImagePreview] = useState<string | null>(null);
  const [reyFigureMemoriaDiferidaImage, setReyFigureMemoriaDiferidaImage] = useState<File | null>(null);
  const [reyFigureMemoriaDiferidaImagePreview, setReyFigureMemoriaDiferidaImagePreview] = useState<string | null>(null);
  const [reyFigurePercentiles, setReyFigurePercentiles] = useState<{
    copiaExactitud?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    copiaTiempo?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    memoriaTipoConstruccion?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    memoriaExactitud?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    memoriaInmediata?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
  }>({});
  const [calculatingReyFigurePercentile, setCalculatingReyFigurePercentile] = useState<string | null>(null);

  /** Grid 18×3: puntuación por elemento (0, 0.5, 1, 2, '' o '0.' intermedio) */
  const [reyFigureGrid, setReyFigureGrid] = useState<{ copia: (number | '' | '0.')[]; memoriaInmediata: (number | '' | '0.')[]; memoriaDiferida: (number | '' | '0.')[] }>({
    copia: Array(18).fill(''),
    memoriaInmediata: Array(18).fill(''),
    memoriaDiferida: Array(18).fill('')
  });
  /** Total manual por columna (si el evaluador escribe solo el total) */
  const [reyFigureManualTotals, setReyFigureManualTotals] = useState<{ copia?: number; memoriaInmediata?: number; memoriaDiferida?: number }>({});
  /** Tiempos totales por columna (minutos) */
  const [reyFigureTiempos, setReyFigureTiempos] = useState<{ copia: number | ''; memoriaInmediata: number | ''; memoriaDiferida: number | '' }>({
    copia: '',
    memoriaInmediata: '',
    memoriaDiferida: ''
  });
  /** Errores de validación por celda (ej. puntuación inválida) */
  const [reyFigureCellErrors, setReyFigureCellErrors] = useState<{ [key: string]: string }>({});
  /** Menú dropdown de puntuación (0, 0.5, 1, 2) por celda */
  const [reyFigureScoreMenu, setReyFigureScoreMenu] = useState<{ anchorEl: HTMLElement | null; row: number; col: ReyCol }>({ anchorEl: null, row: -1, col: 'copia' });

  /** Totales por columna: suma de 18 ítems o total manual (tratamos '0.' como incompleto) */
  const reyFigureTotalCopia = useMemo(() => {
    if (reyFigureManualTotals.copia !== undefined) return reyFigureManualTotals.copia;
    const arr = reyFigureGrid.copia;
    if (arr.some(x => x === '' || x === '0.')) return undefined;
    return arr.reduce<number>((a, b) => a + (typeof b === 'number' ? b : 0), 0);
  }, [reyFigureGrid.copia, reyFigureManualTotals.copia]);
  const reyFigureTotalMemoriaInmediata = useMemo(() => {
    if (reyFigureManualTotals.memoriaInmediata !== undefined) return reyFigureManualTotals.memoriaInmediata;
    const arr = reyFigureGrid.memoriaInmediata;
    if (arr.some(x => x === '' || x === '0.')) return undefined;
    return arr.reduce<number>((a, b) => a + (typeof b === 'number' ? b : 0), 0);
  }, [reyFigureGrid.memoriaInmediata, reyFigureManualTotals.memoriaInmediata]);
  const reyFigureTotalMemoriaDiferida = useMemo(() => {
    if (reyFigureManualTotals.memoriaDiferida !== undefined) return reyFigureManualTotals.memoriaDiferida;
    const arr = reyFigureGrid.memoriaDiferida;
    if (arr.some(x => x === '' || x === '0.')) return undefined;
    return arr.reduce<number>((a, b) => a + (typeof b === 'number' ? b : 0), 0);
  }, [reyFigureGrid.memoriaDiferida, reyFigureManualTotals.memoriaDiferida]);

  /** Si hay al menos un valor en la columna, el puntaje total es solo lectura (muestra la suma) */
  const reyFigureColumnHasAnyScore = useMemo(() => ({
    copia: reyFigureGrid.copia.some(x => x !== '' && x !== '0.'),
    memoriaInmediata: reyFigureGrid.memoriaInmediata.some(x => x !== '' && x !== '0.'),
    memoriaDiferida: reyFigureGrid.memoriaDiferida.some(x => x !== '' && x !== '0.')
  }), [reyFigureGrid]);
  /** Suma parcial por columna (para mostrar en total cuando hay celdas con valor pero no todas completas) */
  const reyFigurePartialSum = useMemo(() => ({
    copia: reyFigureGrid.copia.reduce<number>((a, b) => a + (typeof b === 'number' ? b : 0), 0),
    memoriaInmediata: reyFigureGrid.memoriaInmediata.reduce<number>((a, b) => a + (typeof b === 'number' ? b : 0), 0),
    memoriaDiferida: reyFigureGrid.memoriaDiferida.reduce<number>((a, b) => a + (typeof b === 'number' ? b : 0), 0)
  }), [reyFigureGrid]);

  /** Sincronizar formData y baremos cuando cambian totales/tiempos (Rey Figure). Mismo baremo memoriaExactitud para M. Inmediata y M. Diferida. */
  useEffect(() => {
    if (formData.testTypeCode !== 'REY-FIGURE') return;
    if (reyFigureTotalCopia != null) {
      setFormData(prev => ({ ...prev, testData: { ...prev.testData, copiaExactitud: reyFigureTotalCopia } }));
      calculateReyFigurePercentile('copiaExactitud', reyFigureTotalCopia);
    }
    if (reyFigureTiempos.copia !== '' && reyFigureTiempos.copia != null) {
      const t = Number(reyFigureTiempos.copia);
      setFormData(prev => ({ ...prev, testData: { ...prev.testData, copiaTiempo: t } }));
      calculateReyFigurePercentile('copiaTiempo', t);
    }
    if (reyFigureTotalMemoriaInmediata != null) {
      calculateReyFigurePercentile('memoriaExactitud', reyFigureTotalMemoriaInmediata, 'memoriaInmediata');
    }
    if (reyFigureTotalMemoriaDiferida != null) {
      setFormData(prev => ({ ...prev, testData: { ...prev.testData, memoriaExactitud: reyFigureTotalMemoriaDiferida } }));
      calculateReyFigurePercentile('memoriaExactitud', reyFigureTotalMemoriaDiferida);
    }
  }, [reyFigureTotalCopia, reyFigureTotalMemoriaInmediata, reyFigureTotalMemoriaDiferida, reyFigureTiempos.copia]);
  
  // Para BNT: estados
  const [bntResponses, setBntResponses] = useState<{
    [wordIndex: number]: {
      respuestaCorrecta: boolean;
      claveSemantica: boolean;
      claveFonetica: boolean;
      parafasiasFonologica: boolean;
      parafasiasVerbales: boolean;
    };
  }>({});
  const [bntPercentile, setBntPercentile] = useState<{
    percentile: number | string | null;
    isRange: boolean;
    classifications: string[];
  } | null>(null);
  const [calculatingBntPercentile, setCalculatingBntPercentile] = useState(false);

  // Para MoCA: estados
  const [mocaPercentile, setMocaPercentile] = useState<{
    percentile: number | string | null;
    isRange: boolean;
    classifications: string[];
    rawScore?: number;
    educationAdjustment?: number;
    adjustedScore?: number;
  } | null>(null);
  const [calculatingMocaPercentile, setCalculatingMocaPercentile] = useState(false);
  const [mocaDetailed, setMocaDetailed] = useState<{
    trail: boolean; cube: boolean; clockContour: boolean; clockNumbers: boolean; clockHands: boolean;
    lion: boolean; rhino: boolean; camel: boolean;
    memoriaAttempt1: boolean[]; memoriaAttempt2: boolean[];
    digitsForward: boolean; digitsBackward: boolean;
    letterTap: boolean;
    serial7: boolean[];
    sentence1: boolean; sentence2: boolean;
    fluencyCount: number | undefined;
    abstraction1: boolean; abstraction2: boolean;
    recall: boolean[];
    recallCategoryCue: boolean[];
    recallMultipleChoiceCue: boolean[];
    orientationDay: boolean; orientationMonth: boolean; orientationYear: boolean;
    orientationWeekday: boolean; orientationPlace: boolean; orientationCity: boolean;
  }>({
    trail: false, cube: false, clockContour: false, clockNumbers: false, clockHands: false,
    lion: false, rhino: false, camel: false,
    memoriaAttempt1: Array(5).fill(false), memoriaAttempt2: Array(5).fill(false),
    digitsForward: false, digitsBackward: false,
    letterTap: false,
    serial7: Array(5).fill(false),
    sentence1: false, sentence2: false,
    fluencyCount: undefined,
    abstraction1: false, abstraction2: false,
    recall: Array(5).fill(false),
    recallCategoryCue: Array(5).fill(false),
    recallMultipleChoiceCue: Array(5).fill(false),
    orientationDay: false, orientationMonth: false, orientationYear: false,
    orientationWeekday: false, orientationPlace: false, orientationCity: false,
  });

  // Para IFS-Ch: percentiles por subtest (8 subtests + total), cada uno vía Z-score
  // contra la media/DE de sujetos control (no requiere edad ni escolaridad)
  const [ifsChPercentiles, setIfsChPercentiles] = useState<Record<string, {
    percentile: number | string | null;
    isRange: boolean;
    classifications: string[];
    zScore?: number | null;
    matrices?: Record<string, {
      classification: string;
      percentileRange: string | null;
      tScore: { average: number | null };
      standardScore: { average: number | null };
      scaledScore: { average: number | null };
    } | null>;
  }>>({});
  const [calculatingIfsChPercentile, setCalculatingIfsChPercentile] = useState<string | null>(null);

  const [elevatorResponses, setElevatorResponses] = useState<{
    versionA: boolean[];
    versionB: boolean[];
  }>({
    versionA: Array(7).fill(false),
    versionB: Array(10).fill(false),
  });
  const [elevatorPercentiles, setElevatorPercentiles] = useState<{
    versionA?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
    versionB?: { percentile: number | string | null; isRange: boolean; classifications: string[] };
  }>({});
  const [calculatingElevatorPercentile, setCalculatingElevatorPercentile] = useState<ElevatorVersionKey | null>(null);
  
  // Estado para modal de imagen preview
  const [imagePreviewModal, setImagePreviewModal] = useState<{
    open: boolean;
    imageUrl: string | null;
    title: string;
  }>({
    open: false,
    imageUrl: null,
    title: ''
  });

  const steps = ['Seleccionar Prueba', 'Registrar Resultados', 'Confirmar'];

  // Evita que el scroll del mouse sobre un input numérico cambie su valor (comportamiento
  // nativo del navegador cuando el input está enfocado): simplemente le quita el foco.
  const preventNumberInputScroll = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  };

  // Colores de clasificación (mismos que la matriz de conversión)
  const classificationColors: { [key: string]: string } = {
    'Excepcionalmente Alto': '#dc6eee', // Purple
    'Sobre el Promedio': '#2196F3', // Blue
    'Promedio Alto': '#6dc7f0', // Light Blue
    'Promedio': '#4CAF50', // Green
    'Promedio Bajo': '#FFEB3B', // Yellow
    'Bajo el Promedio': '#FF9800', // Orange
    'Excepcionalmente Bajo': '#F44336', // Red
    'Dentro de Expectativas Normales / Dentro de Límites Normales': '#26A69A' // Teal
  };

  const matrixTypeLabels: { [key: string]: string } = {
    original: 'Matriz Original',
    normal: 'Distribuciones Normales',
    'non-normal': 'Distribuciones No Normales'
  };

  // "Modo compare" (ver getTestResultMode en types/test.ts): 3 cuadros de referencia, uno
  // por matriz de clasificación, para el mismo percentil (independiente de la matriz).
  // Genérico: no depende de nada específico de IFS-Ch, cualquier prueba en modo 'compare'
  // puede reutilizarlo con el `matrices` que devuelva su cálculo de percentil.
  const renderMatrixComparisonBoxes = (matrices?: Record<string, {
    classification: string;
    percentileRange: string | null;
    tScore: { average: number | null };
    standardScore: { average: number | null };
    scaledScore: { average: number | null };
  } | null>) => {
    if (!matrices) return null;
    const types: Array<'original' | 'normal' | 'non-normal'> = ['original', 'normal', 'non-normal'];
    return (
      <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
        {types.map((type) => {
          const data = matrices[type];
          return (
            <Card
              key={type}
              variant="outlined"
              sx={{ p: 1, flex: '1 1 200px', minWidth: 180, borderColor: data ? (classificationColors[data.classification] || 'divider') : 'divider' }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block' }}>
                {matrixTypeLabels[type]}
              </Typography>
              {data ? (
                <>
                  <Chip
                    label={data.classification}
                    size="small"
                    sx={{ bgcolor: classificationColors[data.classification] || '#757575', color: 'white', fontWeight: 'bold', mt: 0.5, mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {[
                      data.tScore.average !== null ? `T: ${data.tScore.average}` : null,
                      data.standardScore.average !== null ? `Est: ${data.standardScore.average}` : null,
                      data.scaledScore.average !== null ? `Esc: ${data.scaledScore.average}` : null,
                    ].filter(Boolean).join(' · ') || `Percentil: ${data.percentileRange ?? 'N/A'}`}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">Sin datos</Typography>
              )}
            </Card>
          );
        })}
      </Box>
    );
  };

  // Calcular edad del paciente basada en fecha de nacimiento
  const calculateAge = (dateOfBirth?: string): number | null => {
    if (!dateOfBirth) return null;
    try {
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch (error) {
      return null;
    }
  };

  const patientAge = calculateAge(patientDateOfBirth);

  // Calcular percentil desde el backend (preview) - Para TMT
  const calculatePercentile = async (timeSeconds: number, testTypeCode: string) => {
    if (!patientEducationYears || !patientId || timeSeconds <= 0) {
      setCalculatedPercentile(null);
      return;
    }

    // Solo calcular para TMT-A y TMT-B
    if (testTypeCode !== 'TMT-A' && testTypeCode !== 'TMT-B') {
      setCalculatedPercentile(null);
      return;
    }

    try {
      setCalculatingPercentile(true);
      
      // Llamar al backend para calcular percentil y clasificación(es)
      const result = await testService.calculatePercentilePreview({
        matrixType,
        testTypeCode,
        patientId,
        timeSeconds: Math.round(timeSeconds), // Redondear a segundos enteros
        educationYears: patientEducationYears
      });

      if (result && result.percentile !== null && result.percentile !== undefined) {
        setCalculatedPercentile({
          percentile: result.percentile,
          isRange: result.isRange || false,
          classifications: result.classifications || []
        });
      } else {
        setCalculatedPercentile(null);
      }
    } catch (error: any) {
      console.error('Error calculando percentil:', error);
      setCalculatedPercentile(null);
    } finally {
      setCalculatingPercentile(false);
    }
  };

  // Calcular percentil Stroop para una condición específica
  const calculateStroopPercentile = async (condition: string, score: number) => {
    if (!patientEducationYears || !patientId || score <= 0 || !patientAge) {
      setStroopPercentiles(prev => {
        const updated = { ...prev };
        delete updated[condition as keyof typeof updated];
        return updated;
      });
      return;
    }

    if (!['palabra', 'color', 'palabraColor'].includes(condition)) {
      return;
    }

    try {
      setCalculatingStroopPercentile(condition);
      
      // Llamar al backend para calcular percentil y clasificación(es)
      const result = await testService.calculatePercentilePreview({
        matrixType,
        testTypeCode: 'STROOP',
        patientId,
        condition,
        score: Math.round(score),
        educationYears: patientEducationYears
      });

      if (result && result.percentile !== null && result.percentile !== undefined) {
        setStroopPercentiles(prev => ({
          ...prev,
          [condition]: {
            percentile: result.percentile,
            isRange: result.isRange || false,
            classifications: result.classifications || []
          }
        }));
      } else {
        setStroopPercentiles(prev => {
          const updated = { ...prev };
          delete updated[condition as keyof typeof updated];
          return updated;
        });
      }
    } catch (error: any) {
      console.error(`Error calculando percentil Stroop-${condition}:`, error);
      setStroopPercentiles(prev => {
        const updated = { ...prev };
        delete updated[condition as keyof typeof updated];
        return updated;
      });
    } finally {
      setCalculatingStroopPercentile(null);
    }
  };

  // Calcular percentil Fluencias para una categoría específica
  const calculateFluenciasPercentile = async (condition: string, score: number) => {
    if (!patientEducationYears || !patientId || score <= 0 || !patientAge) {
      setFluenciasPercentiles(prev => {
        const updated = { ...prev };
        delete updated[condition as keyof typeof updated];
        return updated;
      });
      return;
    }

    if (!['animals', 'letterF', 'letterA', 'letterS'].includes(condition)) {
      return;
    }

    try {
      setCalculatingFluenciasPercentile(condition);
      
      // Llamar al backend para calcular percentil y clasificación(es)
      const result = await testService.calculatePercentilePreview({
        matrixType,
        testTypeCode: 'FLUENCIAS',
        patientId,
        condition,
        score: Math.round(score),
        educationYears: patientEducationYears
      });

      if (result && result.percentile !== null && result.percentile !== undefined) {
        setFluenciasPercentiles(prev => ({
          ...prev,
          [condition]: {
            percentile: result.percentile,
            isRange: result.isRange || false,
            classifications: result.classifications || []
          }
        }));
      } else {
        setFluenciasPercentiles(prev => {
          const updated = { ...prev };
          delete updated[condition as keyof typeof updated];
          return updated;
        });
      }
    } catch (error: any) {
      console.error(`Error calculando percentil Fluencias-${condition}:`, error);
      setFluenciasPercentiles(prev => {
        const updated = { ...prev };
        delete updated[condition as keyof typeof updated];
        return updated;
      });
    } finally {
      setCalculatingFluenciasPercentile(null);
    }
  };

  // Calcular percentil HVLT-R para una condición específica
  const calculateHVLTRPercentile = async (condition: 'recuerdoTotal' | 'recuerdoTardio', score: number) => {
    if (!patientEducationYears || !patientId || score <= 0 || !patientAge) {
      setHvltRPercentiles(prev => {
        const updated = { ...prev };
        delete updated[condition];
        return updated;
      });
      return;
    }

    setCalculatingHVLTRPercentile(condition);

    try {
      const result = await testService.calculatePercentilePreview({
        matrixType,
        testTypeCode: 'HVLT-R',
        patientId,
        condition,
        score: Math.round(score),
        educationYears: patientEducationYears
      });

      if (result && result.percentile !== null && result.percentile !== undefined) {
        setHvltRPercentiles(prev => ({
          ...prev,
          [condition]: {
            percentile: result.percentile,
            isRange: result.isRange || false,
            classifications: result.classifications || []
          }
        }));
      } else {
        setHvltRPercentiles(prev => {
          const updated = { ...prev };
          delete updated[condition];
          return updated;
        });
      }
    } catch (error: any) {
      console.error(`Error calculando percentil HVLT-R-${condition}:`, error);
      setHvltRPercentiles(prev => {
        const updated = { ...prev };
        delete updated[condition];
        return updated;
      });
    } finally {
      setCalculatingHVLTRPercentile(null);
    }
  };

  // Calcular percentil Rey Figure para una condición específica. storeKey permite guardar en otra clave (ej. memoriaExactitud → memoriaInmediata).
  const calculateReyFigurePercentile = async (
    condition: 'copiaExactitud' | 'copiaTiempo' | 'memoriaTipoConstruccion' | 'memoriaExactitud',
    score: number | string,
    storeKey?: 'copiaExactitud' | 'copiaTiempo' | 'memoriaTipoConstruccion' | 'memoriaExactitud' | 'memoriaInmediata'
  ) => {
    const key = storeKey ?? condition;
    if (!patientId || score === undefined || score === null) {
      setReyFigurePercentiles(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      return;
    }
    if (!patientAge) {
      setReyFigurePercentiles(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      return;
    }
    setCalculatingReyFigurePercentile(key);
    try {
      const response = await testService.calculatePercentilePreview({
        matrixType,
        testTypeCode: 'REY-FIGURE',
        patientId,
        condition,
        score: condition === 'memoriaTipoConstruccion' ? String(score) : (typeof score === 'number' ? score : parseFloat(String(score))),
        age: patientAge
      });
      if (response && (response.percentile !== null && response.percentile !== undefined && response.percentile !== '')) {
        const percentileData = {
          percentile: response.percentile,
          isRange: response.isRange || false,
          classifications: response.classifications || []
        };
        setReyFigurePercentiles(prev => ({ ...prev, [key]: percentileData }));
      } else {
        setReyFigurePercentiles(prev => {
          const updated = { ...prev };
          delete updated[key];
          return updated;
        });
      }
    } catch (error: any) {
      console.error(`❌ Error calculando percentil Rey Figure-${condition}:`, error);
      setReyFigurePercentiles(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    } finally {
      setCalculatingReyFigurePercentile(null);
    }
  };

  // Calcular score total del BNT (número de respuestas correctas)
  const calculateBntTotalScore = (): number => {
    let totalScore = 0;
    BNT_WORD_LIST.forEach((_, index) => {
      const response = bntResponses[index];
      if (response && response.respuestaCorrecta) {
        totalScore++;
      }
    });
    return totalScore;
  };

  // Calcular percentil BNT
  const calculateBntPercentile = async (score: number) => {
    if (!patientEducationYears || !patientId || score < 0 || !patientAge) {
      setBntPercentile(null);
      return;
    }

    setCalculatingBntPercentile(true);
    try {
      const response = await testService.calculatePercentilePreview({
        matrixType,
        testTypeCode: 'BNT',
        patientId,
        score,
        educationYears: patientEducationYears,
        age: patientAge
      });

      if (response && (response.percentile !== null && response.percentile !== undefined && response.percentile !== '')) {
        setBntPercentile({
          percentile: response.percentile,
          isRange: response.isRange || false,
          classifications: response.classifications || []
        });
      } else {
        setBntPercentile(null);
      }
    } catch (error: any) {
      console.error('Error calculando percentil BNT:', error);
      setBntPercentile(null);
    } finally {
      setCalculatingBntPercentile(false);
    }
  };

  // Calcular percentil MoCA. El puntaje directo se ajusta automáticamente en el backend
  // según los años de escolaridad del paciente (regla oficial MoCA: +2 si <8 años, +1 si 8-12).
  const calculateMocaPercentile = async (score: number) => {
    if (!patientId || score === undefined || score === null || score < 0 || !patientAge) {
      setMocaPercentile(null);
      return;
    }

    setCalculatingMocaPercentile(true);
    try {
      const response = await testService.calculatePercentilePreview({
        matrixType,
        testTypeCode: 'MOCA',
        patientId,
        score,
        age: patientAge,
        educationYears: patientEducationYears
      });

      if (response && (response.percentile !== null && response.percentile !== undefined && response.percentile !== '')) {
        setMocaPercentile({
          percentile: response.percentile,
          isRange: response.isRange || false,
          classifications: response.classifications || [],
          rawScore: response.rawScore,
          educationAdjustment: response.educationAdjustment,
          adjustedScore: response.adjustedScore
        });
      } else {
        setMocaPercentile(null);
      }
    } catch (error: any) {
      console.error('Error calculando percentil MoCA:', error);
      setMocaPercentile(null);
    } finally {
      setCalculatingMocaPercentile(false);
    }
  };

  // ----- MoCA: Forma Detallada (replica la ficha impresa por dominio) -----
  const MOCA_WORDS = ['Rojo', 'Seda', 'Iglesia', 'Clavel', 'Cara'];
  const MOCA_SERIAL7_VALUES = [93, 86, 79, 72, 65];

  const calculateMocaSerial7Points = (correctCount: number): number => {
    if (correctCount >= 4) return 3;
    if (correctCount >= 2) return 2;
    if (correctCount >= 1) return 1;
    return 0;
  };

  const calculateMocaDetailedTotal = (d: typeof mocaDetailed): number => {
    const visuo = (d.trail ? 1 : 0) + (d.cube ? 1 : 0) + (d.clockContour ? 1 : 0) + (d.clockNumbers ? 1 : 0) + (d.clockHands ? 1 : 0); // /5
    const identificacion = (d.lion ? 1 : 0) + (d.rhino ? 1 : 0) + (d.camel ? 1 : 0); // /3
    const digits = (d.digitsForward ? 1 : 0) + (d.digitsBackward ? 1 : 0); // /2
    const letterTap = d.letterTap ? 1 : 0; // /1
    const serial7Points = calculateMocaSerial7Points(d.serial7.filter(Boolean).length); // /3
    const sentences = (d.sentence1 ? 1 : 0) + (d.sentence2 ? 1 : 0); // /2
    const fluency = (d.fluencyCount !== undefined && d.fluencyCount >= 11) ? 1 : 0; // /1
    const abstraction = (d.abstraction1 ? 1 : 0) + (d.abstraction2 ? 1 : 0); // /2
    const recall = d.recall.filter(Boolean).length; // /5 (sin pistas únicamente)
    const orientation = (d.orientationDay ? 1 : 0) + (d.orientationMonth ? 1 : 0) + (d.orientationYear ? 1 : 0)
      + (d.orientationWeekday ? 1 : 0) + (d.orientationPlace ? 1 : 0) + (d.orientationCity ? 1 : 0); // /6
    return visuo + identificacion + digits + letterTap + serial7Points + sentences + fluency + abstraction + recall + orientation;
  };

  const mocaDetailedTotal = useMemo(() => calculateMocaDetailedTotal(mocaDetailed), [mocaDetailed]);

  // Recalcular cada vez que cambian los checks de la ficha detallada
  useEffect(() => {
    if (formData.testTypeCode !== 'MOCA') return;
    setFormData(prev => ({ ...prev, testData: { ...prev.testData, mocaScore: mocaDetailedTotal, mocaDetailed } }));
    calculateMocaPercentile(mocaDetailedTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mocaDetailedTotal, formData.testTypeCode]);

  const calculateElevatorTotalScore = (version: ElevatorVersionKey): number =>
    elevatorResponses[version].filter(Boolean).length;

  const calculateElevatorPercentile = async (version: ElevatorVersionKey, score: number) => {
    if (!patientId || score < 0 || !patientAge) {
      setElevatorPercentiles(prev => {
        const updated = { ...prev };
        delete updated[version];
        return updated;
      });
      return;
    }

    setCalculatingElevatorPercentile(version);
    try {
      const response = await testService.calculatePercentilePreview({
        matrixType,
        testTypeCode: 'ELEVATOR-3B',
        patientId,
        condition: version,
        score,
        age: patientAge,
      });

      if (response && response.percentile !== null && response.percentile !== undefined) {
        setElevatorPercentiles(prev => ({
          ...prev,
          [version]: {
            percentile: response.percentile,
            isRange: response.isRange || false,
            classifications: response.classifications || [],
          },
        }));
      } else {
        setElevatorPercentiles(prev => {
          const updated = { ...prev };
          delete updated[version];
          return updated;
        });
      }
    } catch (error) {
      setElevatorPercentiles(prev => {
        const updated = { ...prev };
        delete updated[version];
        return updated;
      });
    } finally {
      setCalculatingElevatorPercentile(null);
    }
  };

  // Calcular percentil D2 para una métrica específica
  const calculateD2Percentile = async (condition: 'TR' | 'TA' | 'O' | 'C' | 'TOT' | 'CON' | 'TR+' | 'TR-' | 'VAR', score: number) => {
    if (!patientId || score === undefined || score === null || Number.isNaN(score)) {
      setD2Percentiles(prev => {
        const updated = { ...prev };
        delete updated[condition];
        return updated;
      });
      return;
    }

    try {
      setCalculatingD2Percentile(condition);
      const result = await testService.calculatePercentilePreview({
        matrixType,
        testTypeCode: 'D2',
        patientId,
        condition,
        score: Number(score),
      });

      if (result && result.percentile !== null && result.percentile !== undefined) {
        const percentileText = String(result.percentile);
        const percentileParts = percentileText
          .split(/[\/-]/)
          .map(p => Number(p.trim()))
          .filter(n => !Number.isNaN(n));

        const conversionList: any[] = [];
        for (const p of percentileParts.length > 0 ? percentileParts : [Number(result.percentile)]) {
          if (Number.isNaN(p)) continue;
          try {
            const conversions = await evaluationService.getConversionsByPercentile(p, matrixType);
            conversions.forEach((c: any) => conversionList.push(c));
          } catch {
            // no-op: mantener UX aunque falle la matriz
          }
        }

        setD2Conversions(prev => ({ ...prev, [condition]: conversionList }));
        setD2Percentiles(prev => ({
          ...prev,
          [condition]: {
            percentile: result.percentile,
            isRange: result.isRange || false,
            classifications: result.classifications || [],
          },
        }));
      } else {
        setD2Conversions(prev => ({ ...prev, [condition]: [] }));
        setD2Percentiles(prev => {
          const updated = { ...prev };
          delete updated[condition];
          return updated;
        });
      }
    } catch (error: any) {
      console.error(`Error calculando percentil D2-${condition}:`, error);
      setD2Conversions(prev => ({ ...prev, [condition]: [] }));
      setD2Percentiles(prev => {
        const updated = { ...prev };
        delete updated[condition];
        return updated;
      });
    } finally {
      setCalculatingD2Percentile(null);
    }
  };

  // D2: recalcular TOT y %Error en tiempo real cuando cambian TR/O/C
  useEffect(() => {
    if (formData.testTypeCode !== 'D2') return;

    const tr = Number(formData.testData?.trScore);
    const o = Number(formData.testData?.oScore);
    const c = Number(formData.testData?.cScore);

    const canCalculateTot = !Number.isNaN(tr) && !Number.isNaN(o) && !Number.isNaN(c) && tr > 0 && o >= 0 && c >= 0;
    const nextTot = canCalculateTot ? tr - (o + c) : undefined;
    const nextErrorRate = canCalculateTot ? Math.round((((o + c) * 100) / tr) * 100) / 100 : undefined;
    const ta = Number(formData.testData?.taScore);
    const nextConcentration = !Number.isNaN(ta) && !Number.isNaN(c) ? ta - c : undefined;

    if (formData.testData?.totScore !== nextTot || formData.testData?.errorRate !== nextErrorRate || formData.testData?.concentrationScore !== nextConcentration) {
      setFormData(prev => ({
        ...prev,
        testData: {
          ...prev.testData,
          totScore: nextTot,
          errorRate: nextErrorRate,
          concentrationScore: nextConcentration,
        },
      }));
    }

    if (canCalculateTot && nextTot !== undefined) {
      calculateD2Percentile('TOT', nextTot);
    } else {
      setD2Percentiles(prev => {
        const updated = { ...prev };
        delete updated.TOT;
        return updated;
      });
    }

    if (nextConcentration !== undefined && !Number.isNaN(nextConcentration)) {
      calculateD2Percentile('CON', nextConcentration);
    } else {
      setD2Percentiles(prev => {
        const updated = { ...prev };
        delete updated.CON;
        return updated;
      });
    }
  }, [formData.testTypeCode, formData.testData?.trScore, formData.testData?.taScore, formData.testData?.oScore, formData.testData?.cScore, matrixType]);

  // Al cambiar la matriz de clasificación, recalcular todos los resultados ya ingresados
  // para el tipo de prueba activo (excepto D2 TOT/CON, que ya se recalculan arriba).
  const isFirstMatrixRender = useRef(true);
  useEffect(() => {
    if (isFirstMatrixRender.current) {
      isFirstMatrixRender.current = false;
      return;
    }

    const code = formData.testTypeCode;
    const td = formData.testData || {};

    if (code === 'TMT-A' || code === 'TMT-B') {
      if (td.timeSeconds && td.timeSeconds > 0) {
        calculatePercentile(td.timeSeconds, code);
      }
    } else if (code === 'STROOP') {
      if (td.palabraScore > 0) calculateStroopPercentile('palabra', td.palabraScore);
      if (td.colorScore > 0) calculateStroopPercentile('color', td.colorScore);
      if (td.palabraColorScore > 0) calculateStroopPercentile('palabraColor', td.palabraColorScore);
    } else if (code === 'FLUENCIAS') {
      if (td.animalsScore > 0) calculateFluenciasPercentile('animals', td.animalsScore);
      if (td.letterFScore > 0) calculateFluenciasPercentile('letterF', td.letterFScore);
      if (td.letterAScore > 0) calculateFluenciasPercentile('letterA', td.letterAScore);
      if (td.letterSScore > 0) calculateFluenciasPercentile('letterS', td.letterSScore);
    } else if (code === 'HVLT-R') {
      if (td.recuerdoTotalScore > 0) calculateHVLTRPercentile('recuerdoTotal', td.recuerdoTotalScore);
      if (td.ensayo4Score > 0) calculateHVLTRPercentile('recuerdoTardio', td.ensayo4Score);
    } else if (code === 'REY-FIGURE') {
      if (reyFigureTotalCopia != null) calculateReyFigurePercentile('copiaExactitud', reyFigureTotalCopia);
      if (reyFigureTiempos.copia !== '' && reyFigureTiempos.copia != null) {
        calculateReyFigurePercentile('copiaTiempo', Number(reyFigureTiempos.copia));
      }
      if (reyFigureTotalMemoriaInmediata != null) {
        calculateReyFigurePercentile('memoriaExactitud', reyFigureTotalMemoriaInmediata, 'memoriaInmediata');
      }
      if (reyFigureTotalMemoriaDiferida != null) {
        calculateReyFigurePercentile('memoriaExactitud', reyFigureTotalMemoriaDiferida);
      }
      if (td.copiaTipo && patientAge) calculateReyFigurePercentile('memoriaTipoConstruccion', td.copiaTipo);
    } else if (code === 'BNT') {
      const bntScore = td.bntScore || calculateBntTotalScore();
      if (bntScore >= 0) calculateBntPercentile(bntScore);
    } else if (code === 'MOCA') {
      const mocaScore = td.mocaScore;
      if (mocaScore !== undefined && mocaScore !== null && mocaScore >= 0) calculateMocaPercentile(mocaScore);
    } else if (code === 'ELEVATOR-3B') {
      calculateElevatorPercentile('versionA', calculateElevatorTotalScore('versionA'));
      calculateElevatorPercentile('versionB', calculateElevatorTotalScore('versionB'));
    } else if (code === 'D2') {
      const d2Fields: Record<string, 'TR' | 'TA' | 'O' | 'C' | 'TR+' | 'TR-' | 'VAR'> = {
        trScore: 'TR',
        taScore: 'TA',
        oScore: 'O',
        cScore: 'C',
        trPlusScore: 'TR+',
        trMinusScore: 'TR-',
        varScore: 'VAR',
      };
      Object.entries(d2Fields).forEach(([field, metric]) => {
        const value = td[field];
        if (value !== undefined && value !== null && !Number.isNaN(Number(value)) && Number(value) >= 0) {
          calculateD2Percentile(metric, Number(value));
        }
      });
      // TOT y CON se recalculan automáticamente en el efecto anterior (depende de matrixType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixType]);

  // Manejar cambios en el formulario
  const handleChange = (field: keyof TestFormData, value: any) => {
    let updatedFormData: TestFormData;
    
    if (field === 'testData') {
      updatedFormData = { ...formData, testData: { ...formData.testData, ...value } };
      setFormData(updatedFormData);
      
      // Calcular percentil cuando cambie el tiempo
      if (value.timeSeconds !== undefined && formData.testTypeCode) {
        console.log('handleChange - calculando percentil para tiempo:', value.timeSeconds);
        calculatePercentile(value.timeSeconds, formData.testTypeCode);
      }
    } else {
      updatedFormData = { ...formData, [field]: value };
      setFormData(updatedFormData);
      
      // Calcular percentil cuando cambie el tipo de prueba (si ya hay tiempo)
      if (field === 'testTypeCode' && updatedFormData.testData?.timeSeconds && updatedFormData.testData.timeSeconds > 0) {
        console.log('handleChange - calculando percentil por cambio de tipo:', value);
        calculatePercentile(updatedFormData.testData.timeSeconds, value);
      }
    }
    
    setError(null);
  };

  // Siguiente paso
  const handleNext = () => {
    if (activeStep === 0 && !formData.testTypeCode) {
      setError('Debes seleccionar un tipo de prueba');
      return;
    }
    if (activeStep === 1) {
      // Validación según tipo de prueba
      if (formData.testTypeCode === 'STROOP') {
        // Stroop: validar que al menos las 3 condiciones tengan valores
        const { palabraScore, colorScore, palabraColorScore } = formData.testData || {};
        if (!palabraScore || palabraScore <= 0) {
          setError('La puntuación de Palabra debe ser mayor a 0');
          return;
        }
        if (!colorScore || colorScore <= 0) {
          setError('La puntuación de Color debe ser mayor a 0');
          return;
        }
        if (!palabraColorScore || palabraColorScore <= 0) {
          setError('La puntuación de Palabra-Color debe ser mayor a 0');
          return;
        }
      } else if (formData.testTypeCode === 'FLUENCIAS') {
        // Fluencias: validar que las 4 categorías tengan valores
        const { animalsScore, letterFScore, letterAScore, letterSScore } = formData.testData || {};
        if (!animalsScore || animalsScore <= 0) {
          setError('La puntuación de Animals debe ser mayor a 0');
          return;
        }
        if (!letterFScore || letterFScore <= 0) {
          setError('La puntuación de Letra F debe ser mayor a 0');
          return;
        }
        if (!letterAScore || letterAScore <= 0) {
          setError('La puntuación de Letra A debe ser mayor a 0');
          return;
        }
        if (!letterSScore || letterSScore <= 0) {
          setError('La puntuación de Letra S debe ser mayor a 0');
          return;
        }
      } else if (formData.testTypeCode === 'HVLT-R') {
        // HVLT-R: validar que los 4 ensayos tengan valores
        const { ensayo1Score, ensayo2Score, ensayo3Score, ensayo4Score } = formData.testData || {};
        if (!ensayo1Score || ensayo1Score < 0) {
          setError('El Ensayo 1 debe tener un valor válido (0-12)');
          return;
        }
        if (ensayo1Score === undefined || ensayo1Score > 12) {
          setError('El Ensayo 1 debe estar entre 0 y 12');
          return;
        }
        if (!ensayo2Score || ensayo2Score < 0) {
          setError('El Ensayo 2 debe tener un valor válido (0-12)');
          return;
        }
        if (ensayo2Score === undefined || ensayo2Score > 12) {
          setError('El Ensayo 2 debe estar entre 0 y 12');
          return;
        }
        if (!ensayo3Score || ensayo3Score < 0) {
          setError('El Ensayo 3 debe tener un valor válido (0-12)');
          return;
        }
        if (ensayo3Score === undefined || ensayo3Score > 12) {
          setError('El Ensayo 3 debe estar entre 0 y 12');
          return;
        }
        if (ensayo4Score === undefined || ensayo4Score < 0 || ensayo4Score > 12) {
          setError('El Ensayo 4 (Recuerdo Tardío) debe tener un valor válido (0-12)');
          return;
        }
      } else if (formData.testTypeCode === 'D2') {
        const { trScore, taScore, oScore, cScore } = formData.testData || {};
        if (trScore === undefined || trScore === null || trScore <= 0) {
          setError('TR (Total Respuestas) debe ser mayor a 0');
          return;
        }
        if (taScore === undefined || taScore === null || taScore < 0) {
          setError('TA (Total Aciertos) debe ser mayor o igual a 0');
          return;
        }
        if (oScore === undefined || oScore === null || oScore < 0) {
          setError('O (Omisiones) debe ser mayor o igual a 0');
          return;
        }
        if (cScore === undefined || cScore === null || cScore < 0) {
          setError('C (Comisiones) debe ser mayor o igual a 0');
          return;
        }
      } else if (formData.testTypeCode === 'REY-FIGURE') {
        // Rey Figure: validar campos requeridos (las imágenes NO son obligatorias)
        if (formData.testData?.copiaExactitud === undefined || formData.testData.copiaExactitud === null) {
          setError('La exactitud de copia es requerida');
          return;
        }
        if (formData.testData?.copiaTiempo === undefined || formData.testData.copiaTiempo === null || formData.testData.copiaTiempo <= 0) {
          setError('El tiempo de copia es requerido y debe ser mayor a 0');
          return;
        }
        // Las imágenes NO son obligatorias, se pueden subir después
        if (formData.testData?.memoriaExactitud === undefined || formData.testData.memoriaExactitud === null) {
          setError('La exactitud de memoria es requerida');
          return;
        }
      } else if (formData.testTypeCode === 'BNT') {
        // BNT: validar que se haya evaluado al menos una palabra
        const totalScore = calculateBntTotalScore();
        if (totalScore === undefined || totalScore < 0) {
          setError('Debes evaluar al menos una palabra del BNT');
          return;
        }
        // Guardar el score total en formData si no está guardado
        if (formData.testData?.bntScore === undefined) {
          handleChange('testData', { ...formData.testData, bntScore: totalScore });
        }
      } else if (formData.testTypeCode === 'ELEVATOR-3B') {
        // Elevator 3B: se permite guardar con datos incompletos
      } else if (formData.testTypeCode === 'MOCA') {
        // MoCA: validar puntaje directo total (0-30)
        if (formData.testData?.mocaScore === undefined || formData.testData.mocaScore === null || formData.testData.mocaScore < 0 || formData.testData.mocaScore > 30) {
          setError('El puntaje directo de MoCA debe estar entre 0 y 30');
          return;
        }
      } else if (formData.testTypeCode === 'IFS-CH') {
        // IFS-Ch: validar que los 8 subtests tengan un puntaje dentro de su rango
        const ifsChConfig = TEST_CONFIGS['IFS-CH'];
        for (const condition of ifsChConfig.conditions) {
          const value = formData.testData?.[`${condition.id}Score`];
          if (value === undefined || value === null) {
            setError(`${condition.name} es requerido (0-${condition.max})`);
            return;
          }
          if (value < 0 || value > condition.max) {
            setError(`${condition.name} debe estar entre 0 y ${condition.max}`);
            return;
          }
        }
      } else {
        // TMT: validar tiempo
        if (!formData.testData?.timeSeconds || formData.testData.timeSeconds <= 0) {
          setError('El tiempo debe ser mayor a 0');
          return;
        }
      }
    }
    setActiveStep(prev => prev + 1);
    setError(null);
  };

  // Paso anterior
  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  // Guardar prueba
  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const isStroop = formData.testTypeCode === 'STROOP';
      const isFluencias = formData.testTypeCode === 'FLUENCIAS';
      const isHVLTR = formData.testTypeCode === 'HVLT-R';
      const isD2 = formData.testTypeCode === 'D2';
      const isElevator3B = formData.testTypeCode === 'ELEVATOR-3B';
      
      // Preparar datos según el tipo de prueba
      let testDataToSend: TestFormData;
      
      if (isStroop) {
        // Stroop: enviar los 4 scores (3 condiciones + interferencia)
        testDataToSend = {
          testTypeCode: formData.testTypeCode,
          patientId: formData.patientId,
          testData: {
            matrixType,
            palabraScore: formData.testData?.palabraScore || 0,
            colorScore: formData.testData?.colorScore || 0,
            palabraColorScore: formData.testData?.palabraColorScore || 0,
            interferenciaScore: formData.testData?.interferenciaScore || 0,
            ...(patientEducationYears && { educationYears: patientEducationYears })
          },
          observations: formData.observations
        };
      } else if (isFluencias) {
        // Fluencias: enviar los 4 scores
        testDataToSend = {
          testTypeCode: formData.testTypeCode,
          patientId: formData.patientId,
          testData: {
            matrixType,
            animalsScore: formData.testData?.animalsScore || 0,
            letterFScore: formData.testData?.letterFScore || 0,
            letterAScore: formData.testData?.letterAScore || 0,
            letterSScore: formData.testData?.letterSScore || 0,
            ...(patientEducationYears && { educationYears: patientEducationYears })
          },
          observations: formData.observations
        };
      } else if (isHVLTR) {
        // HVLT-R: enviar los 4 ensayos + recuerdo total y recuerdo tardío + palabras
        testDataToSend = {
          testTypeCode: formData.testTypeCode,
          patientId: formData.patientId,
          testData: {
            matrixType,
            ensayo1Score: formData.testData?.ensayo1Score || 0,
            ensayo2Score: formData.testData?.ensayo2Score || 0,
            ensayo3Score: formData.testData?.ensayo3Score || 0,
            ensayo4Score: formData.testData?.ensayo4Score || 0,
            recuerdoTotalScore: formData.testData?.recuerdoTotalScore || 0,
            recuerdoTardioScore: formData.testData?.ensayo4Score || 0, // Ensayo 4 = Recuerdo Tardío
            ensayo1Words: hvltRWords.ensayo1,
            ensayo2Words: hvltRWords.ensayo2,
            ensayo3Words: hvltRWords.ensayo3,
            ensayo4Words: hvltRWords.ensayo4,
            ...(patientEducationYears && { educationYears: patientEducationYears })
          },
          observations: formData.observations
        };
      } else if (isD2) {
        testDataToSend = {
          testTypeCode: formData.testTypeCode,
          patientId: formData.patientId,
          testData: {
            matrixType,
            trScore: formData.testData?.trScore ?? 0,
            taScore: formData.testData?.taScore ?? 0,
            oScore: formData.testData?.oScore ?? 0,
            cScore: formData.testData?.cScore ?? 0,
            trPlusScore: formData.testData?.trPlusScore ?? null,
            trMinusScore: formData.testData?.trMinusScore ?? null,
            varScore: formData.testData?.varScore ?? null,
            totScore: formData.testData?.totScore ?? null,
            errorRate: formData.testData?.errorRate ?? null,
            concentrationScore: formData.testData?.concentrationScore ?? null,
            trPercentile: d2Percentiles.TR?.percentile ?? null,
            taPercentile: d2Percentiles.TA?.percentile ?? null,
            oPercentile: d2Percentiles.O?.percentile ?? null,
            cPercentile: d2Percentiles.C?.percentile ?? null,
            totPercentile: d2Percentiles.TOT?.percentile ?? null,
            trPlusPercentile: d2Percentiles['TR+']?.percentile ?? null,
            trMinusPercentile: d2Percentiles['TR-']?.percentile ?? null,
            varPercentile: d2Percentiles.VAR?.percentile ?? null,
            concentrationPercentile: d2Percentiles.CON?.percentile ?? null,
            trClassifications: d2Percentiles.TR?.classifications ?? [],
            taClassifications: d2Percentiles.TA?.classifications ?? [],
            oClassifications: d2Percentiles.O?.classifications ?? [],
            cClassifications: d2Percentiles.C?.classifications ?? [],
            totClassifications: d2Percentiles.TOT?.classifications ?? [],
            trPlusClassifications: d2Percentiles['TR+']?.classifications ?? [],
            trMinusClassifications: d2Percentiles['TR-']?.classifications ?? [],
            varClassifications: d2Percentiles.VAR?.classifications ?? [],
            concentrationClassifications: d2Percentiles.CON?.classifications ?? [],
            percentile: d2Percentiles.TOT?.percentile ?? null,
            classifications: d2Percentiles.TOT?.classifications ?? [],
          },
          observations: formData.observations
        };
      } else if (formData.testTypeCode === 'REY-FIGURE') {
        // Rey Figure: JSON ordenado para guardar y revisar después (elementos, totales, tiempos, tipo, percentiles)
        const reyFigureTestData: Record<string, unknown> = {
          matrixType,
          // 1. Datos por elemento (18 ítems) para reconstruir la tabla al ver la prueba
          reyFigureElementos: REY_ELEMENTOS.map((nombre, i) => ({
            numero: i + 1,
            nombre,
            copia: reyFigureGrid.copia[i] === '' || reyFigureGrid.copia[i] === '0.' ? null : reyFigureGrid.copia[i],
            memoriaInmediata: reyFigureGrid.memoriaInmediata[i] === '' || reyFigureGrid.memoriaInmediata[i] === '0.' ? null : reyFigureGrid.memoriaInmediata[i],
            memoriaDiferida: reyFigureGrid.memoriaDiferida[i] === '' || reyFigureGrid.memoriaDiferida[i] === '0.' ? null : reyFigureGrid.memoriaDiferida[i]
          })),
          // 2. Totales por columna
          copiaPuntajeTotal: reyFigureTotalCopia ?? reyFigureManualTotals.copia ?? null,
          memoriaInmediataPuntajeTotal: reyFigureTotalMemoriaInmediata ?? reyFigureManualTotals.memoriaInmediata ?? null,
          memoriaDiferidaPuntajeTotal: reyFigureTotalMemoriaDiferida ?? reyFigureManualTotals.memoriaDiferida ?? null,
          // 3. Tiempos (minutos) por columna
          copiaTiempoMin: reyFigureTiempos.copia === '' ? null : reyFigureTiempos.copia,
          memoriaInmediataTiempoMin: reyFigureTiempos.memoriaInmediata === '' ? null : reyFigureTiempos.memoriaInmediata,
          memoriaDiferidaTiempoMin: reyFigureTiempos.memoriaDiferida === '' ? null : reyFigureTiempos.memoriaDiferida,
          // 4. Tipo de copia (solo columna Copia)
          copiaTipo: formData.testData?.copiaTipo ?? null,
          // 5. Campos usados por el backend para percentiles y búsquedas
          copiaExactitud: formData.testData?.copiaExactitud ?? null,
          copiaTiempo: formData.testData?.copiaTiempo ?? null,
          memoriaExactitud: formData.testData?.memoriaExactitud ?? null,
          // 6. Percentiles y clasificaciones (para no recalcular al visualizar)
          copiaExactitudPercentile: reyFigurePercentiles.copiaExactitud?.percentile ?? null,
          copiaExactitudClassifications: reyFigurePercentiles.copiaExactitud?.classifications ?? [],
          copiaTiempoPercentile: reyFigurePercentiles.copiaTiempo?.percentile ?? null,
          copiaTiempoClassifications: reyFigurePercentiles.copiaTiempo?.classifications ?? [],
          copiaTipoPercentile: reyFigurePercentiles.memoriaTipoConstruccion?.percentile ?? null,
          copiaTipoClassifications: reyFigurePercentiles.memoriaTipoConstruccion?.classifications ?? [],
          memoriaInmediataPercentile: reyFigurePercentiles.memoriaInmediata?.percentile ?? null,
          memoriaInmediataClassifications: reyFigurePercentiles.memoriaInmediata?.classifications ?? [],
          memoriaDiferidaPercentile: reyFigurePercentiles.memoriaExactitud?.percentile ?? null,
          memoriaDiferidaClassifications: reyFigurePercentiles.memoriaExactitud?.classifications ?? []
        };
        // Las URLs de imágenes las agrega el backend después de subir a S3 (copiaImageUrl, memoriaInmediataImageUrl, memoriaDiferidaImageUrl)
        testDataToSend = {
          testTypeCode: formData.testTypeCode,
          patientId: formData.patientId,
          testData: reyFigureTestData,
          observations: formData.observations
        };
      } else if (formData.testTypeCode === 'BNT') {
        // BNT: enviar score total, respuestas detalladas y percentil/clasificaciones
        const totalScore = formData.testData?.bntScore || calculateBntTotalScore();
        testDataToSend = {
          testTypeCode: formData.testTypeCode,
          patientId: formData.patientId,
          testData: {
            matrixType,
            bntScore: totalScore,
            bntResponses: bntResponses, // Todas las respuestas detalladas
            bntPercentile: bntPercentile?.percentile,
            bntClassifications: bntPercentile?.classifications,
            ...(patientEducationYears && { educationYears: patientEducationYears })
          },
          observations: formData.observations
        };
      } else if (formData.testTypeCode === 'MOCA') {
        // MoCA: enviar puntaje directo total (ajustado en el backend por escolaridad) y percentil/clasificaciones
        testDataToSend = {
          testTypeCode: formData.testTypeCode,
          patientId: formData.patientId,
          testData: {
            matrixType,
            mocaEntryMode: 'detallada',
            mocaScore: formData.testData?.mocaScore ?? null,
            mocaDetailed,
            percentile: mocaPercentile?.percentile ?? null,
            classifications: mocaPercentile?.classifications ?? [],
            mocaRawScore: mocaPercentile?.rawScore ?? null,
            mocaEducationAdjustment: mocaPercentile?.educationAdjustment ?? null,
            mocaAdjustedScore: mocaPercentile?.adjustedScore ?? null,
          },
          observations: formData.observations
        };
      } else if (formData.testTypeCode === 'IFS-CH') {
        // IFS-Ch: enviar los 8 subtests + total + índice de memoria de trabajo, cada uno con su Z/percentil
        const ifsChConfig = TEST_CONFIGS['IFS-CH'];
        const ifsChTestData: Record<string, unknown> = { matrixType };
        ifsChConfig.conditions.forEach((condition) => {
          const key = condition.id;
          ifsChTestData[`${key}Score`] = formData.testData?.[`${key}Score`] ?? null;
          ifsChTestData[`${key}Percentile`] = ifsChPercentiles[key]?.percentile ?? null;
          ifsChTestData[`${key}ZScore`] = ifsChPercentiles[key]?.zScore ?? null;
          ifsChTestData[`${key}Classifications`] = ifsChPercentiles[key]?.classifications ?? [];
        });
        ifsChTestData.ifsChTotalScore = ifsChTotalScore ?? null;
        ifsChTestData.score = ifsChTotalScore ?? null;
        ifsChTestData.percentile = ifsChPercentiles.total?.percentile ?? null;
        ifsChTestData.classifications = ifsChPercentiles.total?.classifications ?? [];
        ifsChTestData.ifsChTotalZScore = ifsChPercentiles.total?.zScore ?? null;
        ifsChTestData.ifsChWorkingMemoryIndex = ifsChWorkingMemoryIndex ?? null;

        testDataToSend = {
          testTypeCode: formData.testTypeCode,
          patientId: formData.patientId,
          testData: ifsChTestData,
          observations: formData.observations
        };
      } else if (isElevator3B) {
        const versionAScore = calculateElevatorTotalScore('versionA');
        const versionBScore = calculateElevatorTotalScore('versionB');
        testDataToSend = {
          testTypeCode: formData.testTypeCode,
          patientId: formData.patientId,
          testData: {
            matrixType,
            elevatorVersionAResponses: elevatorResponses.versionA,
            elevatorVersionBResponses: elevatorResponses.versionB,
            elevatorVersionAScore: versionAScore,
            elevatorVersionBScore: versionBScore,
            elevatorVersionAPercentile: elevatorPercentiles.versionA?.percentile ?? null,
            elevatorVersionBPercentile: elevatorPercentiles.versionB?.percentile ?? null,
            elevatorVersionAClassifications: elevatorPercentiles.versionA?.classifications ?? [],
            elevatorVersionBClassifications: elevatorPercentiles.versionB?.classifications ?? [],
            percentile: elevatorPercentiles.versionB?.percentile ?? elevatorPercentiles.versionA?.percentile ?? null,
            classifications: elevatorPercentiles.versionB?.classifications ?? elevatorPercentiles.versionA?.classifications ?? [],
          },
          observations: formData.observations
        };
      } else {
        // TMT: enviar tiempo y errores
        testDataToSend = {
          testTypeCode: formData.testTypeCode,
          patientId: formData.patientId,
          testData: {
            matrixType,
            timeSeconds: formData.testData?.timeSeconds,
            errors: formData.testData?.errors || 0,
            ...(patientEducationYears && { educationYears: patientEducationYears })
          },
          observations: formData.observations
        };
      }
      
      const newTest = await testService.createTest(
        testDataToSend,
        formData.testTypeCode === 'REY-FIGURE' 
          ? ({ 
              copia: reyFigureCopiaImage || undefined,
              memoriaInmediata: reyFigureMemoriaInmediataImage || undefined,
              memoriaDiferida: reyFigureMemoriaDiferidaImage || undefined
            } as Parameters<typeof testService.createTest>[1])
          : undefined
      );
      
      if (onTestAdded) {
        onTestAdded(newTest);
      }
      
      handleClose();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al guardar la prueba');
    } finally {
      setLoading(false);
    }
  };

  // Cerrar modal
  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      testTypeCode: 'TMT-A',
      patientId,
      testData: {
        timeSeconds: undefined,
        errors: undefined,
        palabraScore: 0,
        colorScore: 0,
        palabraColorScore: 0,
        interferenciaScore: 0,
        animalsScore: undefined,
        letterFScore: undefined,
        letterAScore: undefined,
        letterSScore: undefined,
        ensayo1Score: 0,
        ensayo2Score: 0,
        ensayo3Score: 0,
        ensayo4Score: 0,
        recuerdoTotalScore: 0,
        recuerdoTardioScore: 0,
        trScore: undefined,
        taScore: undefined,
        oScore: undefined,
        cScore: undefined,
        trPlusScore: undefined,
        trMinusScore: undefined,
        varScore: undefined,
        totScore: undefined,
        errorRate: undefined,
        concentrationScore: undefined
      },
      observations: ''
    });
    // Resetear palabras de HVLT-R
    setHvltRWords({
      ensayo1: [],
      ensayo2: [],
      ensayo3: [],
      ensayo4: []
    });
    setCalculatedPercentile(null);
    setStroopPercentiles({});
    setFluenciasPercentiles({});
    setHvltRPercentiles({});
    setD2Percentiles({});
    setReyFigurePercentiles({});
    setReyFigureGrid({ copia: Array(18).fill(''), memoriaInmediata: Array(18).fill(''), memoriaDiferida: Array(18).fill('') });
    setReyFigureManualTotals({});
    setReyFigureTiempos({ copia: '', memoriaInmediata: '', memoriaDiferida: '' });
    setReyFigureCellErrors({});
    setReyFigureCopiaImage(null);
    setReyFigureCopiaImagePreview(null);
    setReyFigureMemoriaInmediataImage(null);
    setReyFigureMemoriaInmediataImagePreview(null);
    setReyFigureMemoriaDiferidaImage(null);
    setReyFigureMemoriaDiferidaImagePreview(null);
    setElevatorResponses({
      versionA: Array(7).fill(false),
      versionB: Array(10).fill(false),
    });
    setElevatorPercentiles({});
    setError(null);
    onClose();
  };

  // Calcular interferencia del Stroop cuando cambian los scores
  const updateStroopInterference = (palabraScore: number, colorScore: number, palabraColorScore: number) => {
    const interference = calculateStroopInterference(palabraScore, colorScore, palabraColorScore);
    setFormData(prev => ({
      ...prev,
      testData: {
        ...prev.testData,
        interferenciaScore: interference
      }
    }));
  };

  /** Valida que la puntuación sea 0, 0.5, 1 o 2 */
  const isValidReyScore = (v: number): boolean => (REY_SCORES as readonly number[]).includes(v);
  /** Solo se permite 1 carácter salvo para 0,5: válidos '', '0', '0.', '0.5', '1', '2' */
  const isValidReyInput = (raw: string): boolean => ['', '0', '0.', '0.5', '1', '2'].includes(raw);
  /** Convierte input a valor de celda: '' | 0 | '0.' | 0.5 | 1 | 2 */
  const reyInputToValue = (raw: string): number | '' | '0.' => {
    if (raw === '') return '';
    if (raw === '0.') return '0.';
    const n = parseFloat(raw);
    return isValidReyScore(n) ? n : '';
  };
  const clearReyColumn = (col: ReyCol) => {
    setReyFigureGrid(prev => ({ ...prev, [col]: Array(18).fill('') }));
    setReyFigureManualTotals(prev => {
      const next = { ...prev };
      delete next[col];
      return next;
    });
    setReyFigureCellErrors(prev => {
      const next = { ...prev };
      REY_ELEMENTOS.forEach((_, row) => delete next[`${col}-${row}`]);
      return next;
    });
  };
  /** Siguiente celda en la tabla Rey: abajo en la misma columna; al llegar al 18, pasa a la columna siguiente, fila 1 */
  const getNextReyCell = (row: number, col: ReyCol): { row: number; col: ReyCol } | null => {
    if (row < 17) return { row: row + 1, col };
    const idx = REY_COLS.indexOf(col) + 1;
    if (idx < REY_COLS.length) return { row: 0, col: REY_COLS[idx] };
    return null;
  };
  /** Celda anterior: arriba en la misma columna; en fila 0 pasa a la columna anterior, fila 17 */
  const getPrevReyCell = (row: number, col: ReyCol): { row: number; col: ReyCol } | null => {
    if (row > 0) return { row: row - 1, col };
    const idx = REY_COLS.indexOf(col) - 1;
    if (idx >= 0) return { row: 17, col: REY_COLS[idx] };
    return null;
  };

  // Nombre en inglés de cada subtest en ifs_ch_norms (subtest_en), usado como "condition"
  // al llamar al backend. La clave de estado (storeKey) es el id camelCase del subtest.
  const IFS_CH_SUBTEST_EN: Record<string, string> = {
    luriaMotorSeries: 'Luria motor series',
    conflictingInstructions: 'Conflicting instructions',
    goNoGo: 'Go-No go',
    backwardsDigitSpan: 'Backwards digit span',
    monthsBackwards: 'Months backwards',
    modifiedCorsi: 'Modified Corsi tapping test',
    proverbInterpretation: 'Proverb interpretation',
    modifiedHayling: 'Modified Hayling test',
  };

  // Calcular percentil IFS-Ch para un subtest (storeKey camelCase, ej. 'luriaMotorSeries')
  // o el total (storeKey='total'). No requiere edad/escolaridad: el backend compara
  // contra la media/DE de sujetos control (ifs_ch_norms).
  const calculateIfsChPercentile = async (storeKey: string, score: number) => {
    if (!patientId || score === undefined || score === null || Number.isNaN(score)) {
      setIfsChPercentiles(prev => {
        const updated = { ...prev };
        delete updated[storeKey];
        return updated;
      });
      return;
    }

    const condition = storeKey === 'total' ? 'Total IFS-Ch' : IFS_CH_SUBTEST_EN[storeKey];
    if (!condition) return;

    try {
      setCalculatingIfsChPercentile(storeKey);

      const result = await testService.calculatePercentilePreview({
        matrixType,
        testTypeCode: 'IFS-CH',
        patientId,
        condition,
        score
      });

      if (result && result.percentile !== null && result.percentile !== undefined) {
        setIfsChPercentiles(prev => ({
          ...prev,
          [storeKey]: {
            percentile: result.percentile,
            isRange: result.isRange || false,
            classifications: result.classifications || [],
            zScore: result.zScore ?? null,
            matrices: result.matrices ?? {}
          }
        }));
      } else {
        setIfsChPercentiles(prev => {
          const updated = { ...prev };
          delete updated[storeKey];
          return updated;
        });
      }
    } catch (error: any) {
      console.error(`Error calculando percentil IFS-Ch-${storeKey}:`, error);
      setIfsChPercentiles(prev => {
        const updated = { ...prev };
        delete updated[storeKey];
        return updated;
      });
    } finally {
      setCalculatingIfsChPercentile(null);
    }
  };

  /** Puntaje total IFS-Ch (0-30): suma de los 8 subtests, solo si todos tienen un valor. */
  const ifsChTotalScore = useMemo(() => {
    const td = formData.testData || {};
    const keys = ['luriaMotorSeries', 'conflictingInstructions', 'goNoGo', 'backwardsDigitSpan', 'monthsBackwards', 'modifiedCorsi', 'proverbInterpretation', 'modifiedHayling'];
    if (keys.some((k) => td[`${k}Score`] === undefined || td[`${k}Score`] === null)) return undefined;
    return keys.reduce((sum, k) => sum + (Number(td[`${k}Score`]) || 0), 0);
  }, [formData.testData]);

  /** Índice de Memoria de Trabajo (Dígitos atrás + Corsi, 0-10): solo informativo, sin baremo propio. */
  const ifsChWorkingMemoryIndex = useMemo(() => {
    const td = formData.testData || {};
    if (td.backwardsDigitSpanScore === undefined || td.backwardsDigitSpanScore === null) return undefined;
    if (td.modifiedCorsiScore === undefined || td.modifiedCorsiScore === null) return undefined;
    return Number(td.backwardsDigitSpanScore) + Number(td.modifiedCorsiScore);
  }, [formData.testData]);

  // Calcular percentil del Total IFS-Ch cuando los 8 subtests están completos
  useEffect(() => {
    if (formData.testTypeCode !== 'IFS-CH') return;
    if (ifsChTotalScore === undefined) {
      setIfsChPercentiles(prev => {
        const updated = { ...prev };
        delete updated.total;
        return updated;
      });
      return;
    }
    calculateIfsChPercentile('total', ifsChTotalScore);
  }, [ifsChTotalScore, formData.testTypeCode, matrixType]);

  // Renderizar contenido del paso
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Seleccionar Tipo de Prueba
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Selecciona el tipo de prueba que deseas aplicar al paciente <strong>{patientName}</strong>
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Tipo de Prueba</InputLabel>
              <Select
                value={formData.testTypeCode || ''}
                onChange={(e) => handleChange('testTypeCode', e.target.value)}
                label="Tipo de Prueba"
              >
                {TEST_TYPE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.testTypeCode && TEST_CONFIGS[formData.testTypeCode as keyof typeof TEST_CONFIGS] && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {TEST_CONFIGS[formData.testTypeCode as keyof typeof TEST_CONFIGS].name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {TEST_CONFIGS[formData.testTypeCode as keyof typeof TEST_CONFIGS].description}
                  </Typography>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Instrucciones:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    {TEST_CONFIGS[formData.testTypeCode as keyof typeof TEST_CONFIGS].instructions.map((instruction, index) => (
                      <li key={index}>
                        <Typography variant="body2">
                          {instruction}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                  
                  {/* Mostrar tiempos solo para TMT (no para Stroop) */}
                  {!isScoreBasedTest(formData.testTypeCode || '') && (
                    <Box display="flex" gap={2} mt={2}>
                      <Chip 
                        icon={<TimerIcon />} 
                        label={`Tiempo esperado: ${(TEST_CONFIGS[formData.testTypeCode as keyof typeof TEST_CONFIGS] as any)?.expectedTime || 0}s`}
                        color="primary"
                        variant="outlined"
                      />
                      <Chip 
                        icon={<ErrorIcon />} 
                        label={`Tiempo máximo: ${(TEST_CONFIGS[formData.testTypeCode as keyof typeof TEST_CONFIGS] as any)?.maxTime || 0}s`}
                        color="secondary"
                        variant="outlined"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        );

      case 1: {
        const isStroop = formData.testTypeCode === 'STROOP';
        const isFluencias = formData.testTypeCode === 'FLUENCIAS';
        const isHVLTR = formData.testTypeCode === 'HVLT-R';
        const isReyFigure = formData.testTypeCode === 'REY-FIGURE';
        const isBNT = formData.testTypeCode === 'BNT';
        const isD2 = formData.testTypeCode === 'D2';
        const isElevator3B = formData.testTypeCode === 'ELEVATOR-3B';
        const isMoca = formData.testTypeCode === 'MOCA';
        const isIFSCh = formData.testTypeCode === 'IFS-CH';
        const stroopConfig = TEST_CONFIGS['STROOP'];
        const fluenciasConfig = TEST_CONFIGS['FLUENCIAS'];
        const hvltRConfig = TEST_CONFIGS['HVLT-R'];
        const ifsChConfig = TEST_CONFIGS['IFS-CH'];
        
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Registrar Resultados
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Ingresa los resultados de la prueba <strong>{formData.testTypeCode && TEST_CONFIGS[formData.testTypeCode as keyof typeof TEST_CONFIGS]?.name}</strong>
            </Typography>

            {getTestResultMode(formData.testTypeCode || '') !== 'compare' && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="matrix-type-label">Matriz de clasificación</InputLabel>
                <Select
                  labelId="matrix-type-label"
                  label="Matriz de clasificación"
                  value={matrixType}
                  onChange={(e) => setMatrixType(e.target.value as 'original' | 'normal' | 'non-normal')}
                >
                  <MenuItem value="original">{matrixTypeLabels.original} (por defecto)</MenuItem>
                  <MenuItem value="normal">{matrixTypeLabels.normal}</MenuItem>
                  <MenuItem value="non-normal">{matrixTypeLabels['non-normal']}</MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Define qué tabla de clasificación se usará para calcular los resultados de esta evaluación.
                </Typography>
              </FormControl>
            )}

            {/* Campos para TMT (tiempo y errores) */}
            {!isStroop && !isFluencias && !isHVLTR && !isReyFigure && !isBNT && !isD2 && !isElevator3B && !isMoca && !isIFSCh && (
              <Box display="flex" gap={2} mb={3}>
                <TextField
                  fullWidth
                  label="Tiempo (segundos)"
                  type="number"
                  onWheel={preventNumberInputScroll}
                  value={formData.testData?.timeSeconds !== undefined && formData.testData?.timeSeconds !== null ? formData.testData.timeSeconds : ''}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    const value = inputValue === '' ? undefined : (parseFloat(inputValue) || undefined);
                    handleChange('testData', { timeSeconds: value });
                  }}
                  inputProps={{ min: 0, step: 1 }}
                  helperText="Ingresa el tiempo en segundos (ej: 45)"
                  InputLabelProps={{ shrink: true }}
                />
                
                <TextField
                  fullWidth
                  label="Errores"
                  type="number"
                  onWheel={preventNumberInputScroll}
                  value={formData.testData?.errors !== undefined && formData.testData?.errors !== null ? formData.testData.errors : ''}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    const value = inputValue === '' ? undefined : (parseInt(inputValue) || 0);
                    handleChange('testData', { errors: value });
                  }}
                  inputProps={{ min: 0 }}
                  helperText="Número de errores cometidos"
                />
              </Box>
            )}

            {/* Campos para MoCA: ficha detallada por dominio (replica el formulario impreso) */}
            {isMoca && (
              <Box mb={3}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  mb: 2, p: 1.5, borderRadius: 1,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                  border: '1px solid', borderColor: 'divider'
                }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    MoCA — Montreal Cognitive Assessment
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Marca cada ítem tal como aparece en la ficha impresa
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Visuoespacial / Ejecutiva */}
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">Visuoespacial / Ejecutiva</Typography>
                      <Chip size="small" label={`${[mocaDetailed.trail, mocaDetailed.cube, mocaDetailed.clockContour, mocaDetailed.clockNumbers, mocaDetailed.clockHands].filter(Boolean).length} / 5`} color="primary" variant="outlined" />
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.trail} onChange={(e) => setMocaDetailed(prev => ({ ...prev, trail: e.target.checked }))} />}
                        label="Trail (1-A-2-B... alternado)"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.cube} onChange={(e) => setMocaDetailed(prev => ({ ...prev, cube: e.target.checked }))} />}
                        label="Copiar el cubo"
                      />
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Dibujar un reloj (Once y diez):
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.clockContour} onChange={(e) => setMocaDetailed(prev => ({ ...prev, clockContour: e.target.checked }))} />}
                        label="Contorno"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.clockNumbers} onChange={(e) => setMocaDetailed(prev => ({ ...prev, clockNumbers: e.target.checked }))} />}
                        label="Números"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.clockHands} onChange={(e) => setMocaDetailed(prev => ({ ...prev, clockHands: e.target.checked }))} />}
                        label="Agujas"
                      />
                    </Box>
                  </Card>

                  {/* Identificación */}
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">Identificación</Typography>
                      <Chip size="small" label={`${[mocaDetailed.lion, mocaDetailed.rhino, mocaDetailed.camel].filter(Boolean).length} / 3`} color="primary" variant="outlined" />
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.lion} onChange={(e) => setMocaDetailed(prev => ({ ...prev, lion: e.target.checked }))} />}
                        label="León"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.rhino} onChange={(e) => setMocaDetailed(prev => ({ ...prev, rhino: e.target.checked }))} />}
                        label="Rinoceronte"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.camel} onChange={(e) => setMocaDetailed(prev => ({ ...prev, camel: e.target.checked }))} />}
                        label="Camello"
                      />
                    </Box>
                  </Card>

                  {/* Memoria (sin puntaje) */}
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                      Memoria (sin puntos — solo registro para el recuerdo diferido)
                    </Typography>
                    {(['memoriaAttempt1', 'memoriaAttempt2'] as const).map((attemptKey, attemptIdx) => (
                      <Box key={attemptKey} display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={attemptIdx === 0 ? 1 : 0}>
                        <Typography variant="caption" sx={{ width: 90 }}>{attemptIdx === 0 ? '1er intento' : '2º intento'}</Typography>
                        {MOCA_WORDS.map((word, wordIdx) => (
                          <FormControlLabel
                            key={word}
                            control={
                              <Checkbox
                                size="small"
                                checked={mocaDetailed[attemptKey][wordIdx]}
                                onChange={(e) => setMocaDetailed(prev => {
                                  const updated = [...prev[attemptKey]];
                                  updated[wordIdx] = e.target.checked;
                                  return { ...prev, [attemptKey]: updated };
                                })}
                              />
                            }
                            label={word}
                          />
                        ))}
                      </Box>
                    ))}
                  </Card>

                  {/* Atención */}
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">Atención</Typography>
                      <Chip
                        size="small"
                        label={`${(mocaDetailed.digitsForward ? 1 : 0) + (mocaDetailed.digitsBackward ? 1 : 0) + (mocaDetailed.letterTap ? 1 : 0) + calculateMocaSerial7Points(mocaDetailed.serial7.filter(Boolean).length)} / 6`}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.digitsForward} onChange={(e) => setMocaDetailed(prev => ({ ...prev, digitsForward: e.target.checked }))} />}
                        label="Repite 2-1-8-5-4 (directo)"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.digitsBackward} onChange={(e) => setMocaDetailed(prev => ({ ...prev, digitsBackward: e.target.checked }))} />}
                        label="Repite 7-4-2 (inverso)"
                      />
                      <Chip size="small" label={`${(mocaDetailed.digitsForward ? 1 : 0) + (mocaDetailed.digitsBackward ? 1 : 0)}/2`} color="info" variant="outlined" />
                    </Box>
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.letterTap} onChange={(e) => setMocaDetailed(prev => ({ ...prev, letterTap: e.target.checked }))} />}
                        label="Golpecito al escuchar la letra A (≤2 errores)"
                      />
                      <Chip size="small" label={`${mocaDetailed.letterTap ? 1 : 0}/1`} color="info" variant="outlined" />
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Resta de 7 en 7 desde 100 (marca cada resta correcta):
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                      {MOCA_SERIAL7_VALUES.map((val, idx) => (
                        <FormControlLabel
                          key={val}
                          control={
                            <Checkbox
                              size="small"
                              checked={mocaDetailed.serial7[idx]}
                              onChange={(e) => setMocaDetailed(prev => {
                                const updated = [...prev.serial7];
                                updated[idx] = e.target.checked;
                                return { ...prev, serial7: updated };
                              })}
                            />
                          }
                          label={val}
                        />
                      ))}
                      <Chip
                        size="small"
                        label={`Puntos: ${calculateMocaSerial7Points(mocaDetailed.serial7.filter(Boolean).length)}/3`}
                        color="info"
                        variant="outlined"
                      />
                    </Box>
                  </Card>

                  {/* Lenguaje */}
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">Lenguaje</Typography>
                      <Chip
                        size="small"
                        label={`${(mocaDetailed.sentence1 ? 1 : 0) + (mocaDetailed.sentence2 ? 1 : 0) + ((mocaDetailed.fluencyCount !== undefined && mocaDetailed.fluencyCount >= 11) ? 1 : 0)} / 3`}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.sentence1} onChange={(e) => setMocaDetailed(prev => ({ ...prev, sentence1: e.target.checked }))} />}
                        label='"El gato se esconde bajo el sofá cuando los perros entran en la sala"'
                      />
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.sentence2} onChange={(e) => setMocaDetailed(prev => ({ ...prev, sentence2: e.target.checked }))} />}
                        label='"Espero que él le entregue el mensaje una vez que ella se lo pida"'
                      />
                      <Chip size="small" label={`${(mocaDetailed.sentence1 ? 1 : 0) + (mocaDetailed.sentence2 ? 1 : 0)}/2`} color="info" variant="outlined" />
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                      <TextField
                        label='Fluidez: palabras con "P" en 1 min'
                        type="number"
                        onWheel={preventNumberInputScroll}
                        size="small"
                        value={mocaDetailed.fluencyCount ?? ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? undefined : Math.max(0, parseInt(e.target.value) || 0);
                          setMocaDetailed(prev => ({ ...prev, fluencyCount: val }));
                        }}
                        helperText="1 punto si ≥ 11 palabras"
                        sx={{ width: 220 }}
                        InputLabelProps={{ shrink: true }}
                      />
                      <Chip size="small" label={`${(mocaDetailed.fluencyCount !== undefined && mocaDetailed.fluencyCount >= 11) ? 1 : 0}/1`} color="info" variant="outlined" />
                    </Box>
                  </Card>

                  {/* Abstracción */}
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">Abstracción</Typography>
                      <Chip size="small" label={`${(mocaDetailed.abstraction1 ? 1 : 0) + (mocaDetailed.abstraction2 ? 1 : 0)} / 2`} color="primary" variant="outlined" />
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.abstraction1} onChange={(e) => setMocaDetailed(prev => ({ ...prev, abstraction1: e.target.checked }))} />}
                        label="Tren - Bicicleta"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={mocaDetailed.abstraction2} onChange={(e) => setMocaDetailed(prev => ({ ...prev, abstraction2: e.target.checked }))} />}
                        label="Reloj - Regla"
                      />
                    </Box>
                  </Card>

                  {/* Recuerdo Diferido */}
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">Recuerdo Diferido</Typography>
                      <Chip size="small" label={`${mocaDetailed.recall.filter(Boolean).length} / 5`} color="primary" variant="outlined" />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Solo cuenta el recuerdo sin pistas:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {MOCA_WORDS.map((word, idx) => (
                        <FormControlLabel
                          key={word}
                          control={
                            <Checkbox
                              checked={mocaDetailed.recall[idx]}
                              onChange={(e) => setMocaDetailed(prev => {
                                const updated = [...prev.recall];
                                updated[idx] = e.target.checked;
                                return { ...prev, recall: updated };
                              })}
                            />
                          }
                          label={word}
                        />
                      ))}
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Pistas (optativo, no suman puntos):
                    </Typography>
                    {(['recallCategoryCue', 'recallMultipleChoiceCue'] as const).map((cueKey, cueIdx) => (
                      <Box key={cueKey} display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                        <Typography variant="caption" sx={{ width: 140 }}>{cueIdx === 0 ? 'Pista de categoría' : 'Elección múltiple'}</Typography>
                        {MOCA_WORDS.map((word, wordIdx) => (
                          <FormControlLabel
                            key={word}
                            control={
                              <Checkbox
                                size="small"
                                checked={mocaDetailed[cueKey][wordIdx]}
                                onChange={(e) => setMocaDetailed(prev => {
                                  const updated = [...prev[cueKey]];
                                  updated[wordIdx] = e.target.checked;
                                  return { ...prev, [cueKey]: updated };
                                })}
                              />
                            }
                            label={word}
                          />
                        ))}
                      </Box>
                    ))}
                  </Card>

                  {/* Orientación */}
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">Orientación</Typography>
                      <Chip
                        size="small"
                        label={`${[mocaDetailed.orientationDay, mocaDetailed.orientationMonth, mocaDetailed.orientationYear, mocaDetailed.orientationWeekday, mocaDetailed.orientationPlace, mocaDetailed.orientationCity].filter(Boolean).length} / 6`}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      <FormControlLabel control={<Checkbox checked={mocaDetailed.orientationDay} onChange={(e) => setMocaDetailed(prev => ({ ...prev, orientationDay: e.target.checked }))} />} label="Día del mes" />
                      <FormControlLabel control={<Checkbox checked={mocaDetailed.orientationMonth} onChange={(e) => setMocaDetailed(prev => ({ ...prev, orientationMonth: e.target.checked }))} />} label="Mes" />
                      <FormControlLabel control={<Checkbox checked={mocaDetailed.orientationYear} onChange={(e) => setMocaDetailed(prev => ({ ...prev, orientationYear: e.target.checked }))} />} label="Año" />
                      <FormControlLabel control={<Checkbox checked={mocaDetailed.orientationWeekday} onChange={(e) => setMocaDetailed(prev => ({ ...prev, orientationWeekday: e.target.checked }))} />} label="Día de la semana" />
                      <FormControlLabel control={<Checkbox checked={mocaDetailed.orientationPlace} onChange={(e) => setMocaDetailed(prev => ({ ...prev, orientationPlace: e.target.checked }))} />} label="Lugar" />
                      <FormControlLabel control={<Checkbox checked={mocaDetailed.orientationCity} onChange={(e) => setMocaDetailed(prev => ({ ...prev, orientationCity: e.target.checked }))} />} label="Comuna" />
                    </Box>
                  </Card>
                </Box>

                <Card sx={{ p: 2, mt: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.12)' : 'success.50', border: '1px solid', borderColor: 'success.main' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" fontWeight="bold">
                      Puntaje Directo Total (calculado)
                    </Typography>
                    <Chip label={`${mocaDetailedTotal} / 30`} color="success" sx={{ fontWeight: 'bold', fontSize: '1rem' }} />
                  </Box>
                </Card>

                {!patientAge && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                    No se pudo calcular la edad del paciente; el percentil no se puede calcular.
                  </Typography>
                )}
              </Box>
            )}

            {/* Campos para IFS-Ch (8 subtests) */}
            {isIFSCh && (
              <Box mb={3}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Registra el puntaje directo de cada subtest, según la ficha de aplicación:
                </Typography>

                <Box display="flex" flexDirection="column" gap={2}>
                  {ifsChConfig.conditions.map((condition) => {
                    const fieldName = `${condition.id}Score`;
                    const rawValue = formData.testData?.[fieldName];
                    const percentileData = ifsChPercentiles[condition.id];

                    return (
                      <Card key={condition.id} variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {condition.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {condition.description}
                            </Typography>
                          </Box>
                          <TextField
                            sx={{ width: 150 }}
                            label={`Puntaje (0-${condition.max})`}
                            type="number"
                            onWheel={preventNumberInputScroll}
                            value={rawValue !== undefined && rawValue !== null ? rawValue : ''}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              const value = inputValue === '' ? undefined : Math.max(0, Math.min(condition.max, parseInt(inputValue) || 0));
                              const newTestData = { ...formData.testData, [fieldName]: value };
                              setFormData(prev => ({ ...prev, testData: newTestData }));

                              if (value !== undefined) {
                                calculateIfsChPercentile(condition.id, value);
                              } else {
                                setIfsChPercentiles(prev => {
                                  const updated = { ...prev };
                                  delete updated[condition.id];
                                  return updated;
                                });
                              }
                            }}
                            inputProps={{ min: 0, max: condition.max }}
                            placeholder={condition.placeholder}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </Box>

                        {/* Mostrar percentil calculado en tiempo real */}
                        {rawValue !== undefined && rawValue !== null && (
                          <Box mt={1} pl={2}>
                            {calculatingIfsChPercentile === condition.id ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <LoadingOrb size={20} />
                                <Typography variant="caption" color="text.secondary">
                                  Calculando percentil...
                                </Typography>
                              </Box>
                            ) : percentileData ? (
                              <>
                                <Chip
                                  label={`Z: ${percentileData.zScore ?? 'N/A'} · Percentil: ${percentileData.percentile ?? 'N/A'}`}
                                  color="info"
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                {renderMatrixComparisonBoxes(percentileData.matrices)}
                              </>
                            ) : null}
                          </Box>
                        )}
                      </Card>
                    );
                  })}

                  {/* Índice de Memoria de Trabajo: Dígitos atrás + Corsi (solo informativo) */}
                  {ifsChWorkingMemoryIndex !== undefined && (
                    <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'success.50', border: '1px solid', borderColor: 'success.main' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                            Índice de Memoria de Trabajo
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Dígitos atrás + Corsi (solo informativo, sin percentil propio)
                          </Typography>
                        </Box>
                        <Chip
                          label={`${ifsChWorkingMemoryIndex} / 10`}
                          color="success"
                          sx={{ fontSize: '1.1rem', fontWeight: 'bold', px: 2, py: 2 }}
                        />
                      </Box>
                    </Card>
                  )}

                  {/* Puntaje Total IFS-Ch: suma automática de los 8 subtests */}
                  {ifsChTotalScore !== undefined && (
                    <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.15)' : 'primary.50', border: '2px solid', borderColor: 'primary.main' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={ifsChPercentiles.total ? 1 : 0}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" color="primary">
                            Puntaje Total IFS-Ch
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Calculado automáticamente: suma de los 8 subtests
                          </Typography>
                        </Box>
                        <Chip
                          label={`${ifsChTotalScore} / 30`}
                          color="primary"
                          sx={{ fontSize: '1.2rem', fontWeight: 'bold', px: 2, py: 2.5 }}
                        />
                      </Box>
                      {calculatingIfsChPercentile === 'total' ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <LoadingOrb size={20} />
                          <Typography variant="caption" color="text.secondary">Calculando percentil del total...</Typography>
                        </Box>
                      ) : ifsChPercentiles.total ? (
                        <>
                          <Chip
                            label={`Z: ${ifsChPercentiles.total.zScore ?? 'N/A'} · Percentil: ${ifsChPercentiles.total.percentile ?? 'N/A'}`}
                            color="info"
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                          {renderMatrixComparisonBoxes(ifsChPercentiles.total.matrices)}
                        </>
                      ) : null}
                    </Card>
                  )}
                </Box>
              </Box>
            )}

            {/* Campos para Stroop (3 condiciones) */}
            {isStroop && (
              <Box mb={3}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Registra los ítems correctos para cada condición:
                </Typography>
                
                <Box display="flex" flexDirection="column" gap={2}>
                  {stroopConfig.conditions.map((condition) => (
                    <Card key={condition.id} variant="outlined" sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box flex={1}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {condition.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {condition.description}
                          </Typography>
                        </Box>
                        <TextField
                          sx={{ width: 150 }}
                          label="Ítems correctos"
                          type="number"
                          onWheel={preventNumberInputScroll}
                          value={formData.testData?.[`${condition.id}Score`] || ''}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 0;
                            const newTestData = { 
                              ...formData.testData, 
                              [`${condition.id}Score`]: value 
                            };
                            setFormData(prev => ({ ...prev, testData: newTestData }));
                            
                            // Calcular percentil inmediatamente cuando hay un valor
                            if (value > 0 && patientEducationYears && patientEducationYears > 0 && patientAge) {
                              calculateStroopPercentile(condition.id, value);
                            } else if (value === 0 || !value) {
                              // Limpiar percentil si se borra el valor
                              setStroopPercentiles(prev => {
                                const updated = { ...prev };
                                delete updated[condition.id as keyof typeof updated];
                                return updated;
                              });
                            }
                            
                            // Calcular interferencia si tenemos los 3 valores
                            const palabraScore = condition.id === 'palabra' ? value : (formData.testData?.palabraScore || 0);
                            const colorScore = condition.id === 'color' ? value : (formData.testData?.colorScore || 0);
                            const palabraColorScore = condition.id === 'palabraColor' ? value : (formData.testData?.palabraColorScore || 0);
                            
                            if (palabraScore > 0 && colorScore > 0 && palabraColorScore > 0) {
                              updateStroopInterference(palabraScore, colorScore, palabraColorScore);
                            }
                          }}
                          inputProps={{ min: 0 }}
                          placeholder={condition.placeholder || "Ej: 112"}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          helperText={formData.testData?.[`${condition.id}Score`] ? undefined : "Ingresa el número de ítems correctos"}
                        />
                      </Box>
                      
                      {/* Mostrar percentil calculado en tiempo real */}
                      {formData.testData?.[`${condition.id}Score`] > 0 && patientEducationYears && patientEducationYears > 0 && (
                        <Box mt={1} pl={2}>
                          {calculatingStroopPercentile === condition.id ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <LoadingOrb size={20} />
                              <Typography variant="caption" color="text.secondary">
                                Calculando percentil...
                              </Typography>
                            </Box>
                          ) : stroopPercentiles[condition.id as keyof typeof stroopPercentiles] ? (
                            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                              <Chip
                                label={`Percentil: ${stroopPercentiles[condition.id as keyof typeof stroopPercentiles]?.percentile || 'N/A'}`}
                                color="info"
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                              />
                              {stroopPercentiles[condition.id as keyof typeof stroopPercentiles]?.classifications && 
                               stroopPercentiles[condition.id as keyof typeof stroopPercentiles]!.classifications.length > 0 && (
                                stroopPercentiles[condition.id as keyof typeof stroopPercentiles]!.classifications.length > 1 ? (
                                  <Chip
                                    label={`Clasificación: ${stroopPercentiles[condition.id as keyof typeof stroopPercentiles]!.classifications.join(' / ')}`}
                                    variant="filled"
                                    size="small"
                                    sx={{
                                      background: `linear-gradient(90deg, ${classificationColors[stroopPercentiles[condition.id as keyof typeof stroopPercentiles]!.classifications[0]] || '#757575'} 0%, ${classificationColors[stroopPercentiles[condition.id as keyof typeof stroopPercentiles]!.classifications[1]] || '#757575'} 100%)`,
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    label={`Clasificación: ${stroopPercentiles[condition.id as keyof typeof stroopPercentiles]!.classifications[0]}`}
                                    variant="filled"
                                    size="small"
                                    sx={{
                                      bgcolor: classificationColors[stroopPercentiles[condition.id as keyof typeof stroopPercentiles]!.classifications[0]] || '#757575',
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                )
                              )}
                            </Box>
                          ) : null}
                        </Box>
                      )}
                    </Card>
                  ))}
                  
                  {/* Mostrar índice de interferencia calculado */}
                  {formData.testData?.palabraScore > 0 && 
                   formData.testData?.colorScore > 0 && 
                   formData.testData?.palabraColorScore > 0 && (
                    <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.15)' : 'primary.50', border: '2px solid', borderColor: 'primary.main' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" color="primary">
                            Índice de Interferencia
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Calculado automáticamente: PC' - PC
                          </Typography>
                        </Box>
                        <Chip
                          label={formData.testData?.interferenciaScore?.toFixed(2) || '0.00'}
                          color={formData.testData?.interferenciaScore >= 0 ? 'success' : 'warning'}
                          sx={{ fontSize: '1.2rem', fontWeight: 'bold', px: 2, py: 2.5 }}
                        />
                      </Box>
                    </Card>
                  )}
                </Box>
              </Box>
            )}

            {/* Campos para Fluencias (4 categorías) */}
            {isFluencias && (
              <Box mb={3}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Registra las palabras correctas para cada categoría:
                </Typography>
                
                <Box display="flex" flexDirection="column" gap={2}>
                  {fluenciasConfig.conditions.map((condition) => {
                    const fieldName = `${condition.id}Score` as 'animalsScore' | 'letterFScore' | 'letterAScore' | 'letterSScore';
                    const conditionId = condition.id as 'animals' | 'letterF' | 'letterA' | 'letterS';
                    
                    return (
                      <Card key={condition.id} variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {condition.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {condition.description}
                            </Typography>
                          </Box>
                          <TextField
                            sx={{ width: 150 }}
                            label="Palabras correctas"
                            type="number"
                            onWheel={preventNumberInputScroll}
                            value={formData.testData?.[fieldName] !== undefined && formData.testData?.[fieldName] !== null ? formData.testData[fieldName] : ''}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              // Si el campo está vacío, guardar undefined en lugar de 0
                              const value = inputValue === '' ? undefined : (parseInt(inputValue) || undefined);
                              const newTestData = { 
                                ...formData.testData, 
                                [fieldName]: value 
                              };
                              setFormData(prev => ({ ...prev, testData: newTestData }));
                              
                              // Calcular percentil inmediatamente cuando hay un valor
                              if (value !== undefined && value !== null && value > 0 && patientEducationYears && patientEducationYears > 0 && patientAge) {
                                calculateFluenciasPercentile(conditionId, value);
                              } else {
                                // Limpiar percentil si se borra el valor
                                setFluenciasPercentiles(prev => {
                                  const updated = { ...prev };
                                  delete updated[conditionId];
                                  return updated;
                                });
                              }
                            }}
                            inputProps={{ min: 0 }}
                            placeholder={condition.placeholder || "Ej: 23"}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            helperText={formData.testData?.[fieldName] ? undefined : "Ingresa el número de palabras correctas"}
                          />
                        </Box>
                        
                        {/* Mostrar percentil calculado en tiempo real */}
                        {formData.testData?.[fieldName] && formData.testData[fieldName] > 0 && patientEducationYears && patientEducationYears > 0 && (
                          <Box mt={1} pl={2}>
                            {calculatingFluenciasPercentile === conditionId ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <LoadingOrb size={20} />
                                <Typography variant="caption" color="text.secondary">
                                  Calculando percentil...
                                </Typography>
                              </Box>
                            ) : fluenciasPercentiles[conditionId] ? (
                              <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                                <Chip
                                  label={`Percentil: ${fluenciasPercentiles[conditionId]?.percentile || 'N/A'}`}
                                  color="info"
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                {fluenciasPercentiles[conditionId]?.classifications && 
                                 fluenciasPercentiles[conditionId]!.classifications.length > 0 && (
                                  fluenciasPercentiles[conditionId]!.classifications.length > 1 ? (
                                    <Chip
                                      label={`Clasificación: ${fluenciasPercentiles[conditionId]!.classifications.join(' / ')}`}
                                      variant="filled"
                                      size="small"
                                      sx={{
                                        background: `linear-gradient(90deg, ${classificationColors[fluenciasPercentiles[conditionId]!.classifications[0]] || '#757575'} 0%, ${classificationColors[fluenciasPercentiles[conditionId]!.classifications[1]] || '#757575'} 100%)`,
                                        color: 'white',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  ) : (
                                    <Chip
                                      label={`Clasificación: ${fluenciasPercentiles[conditionId]!.classifications[0]}`}
                                      variant="filled"
                                      size="small"
                                      sx={{
                                        bgcolor: classificationColors[fluenciasPercentiles[conditionId]!.classifications[0]] || '#757575',
                                        color: 'white',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  )
                                )}
                              </Box>
                            ) : null}
                          </Box>
                        )}
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Campos para D2 */}
            {isD2 && (
              <Box mb={3}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Registra los puntajes D2. TR, TA, O y C son obligatorios.
                </Typography>

                <Box display="flex" flexDirection="column" gap={2}>
                  {(TEST_CONFIGS['D2'] as any).conditions.map((condition: any) => {
                    const fieldMap: Record<string, string> = {
                      TR: 'trScore',
                      TA: 'taScore',
                      O: 'oScore',
                      C: 'cScore',
                      'TR+': 'trPlusScore',
                      'TR-': 'trMinusScore',
                      VAR: 'varScore',
                    };
                    const fieldName = fieldMap[condition.id];
                    const numericValue = formData.testData?.[fieldName];
                    const isRequired = ['TR', 'TA', 'O', 'C'].includes(condition.id);
                    const percentileData = d2Percentiles[condition.id as keyof typeof d2Percentiles];
                    const conversions = d2Conversions[condition.id] || [];

                    return (
                      <Card key={condition.id} variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {condition.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {condition.description}
                            </Typography>
                          </Box>
                          <TextField
                            sx={{ width: 180 }}
                            label={isRequired ? 'Puntaje (obligatorio)' : 'Puntaje (opcional)'}
                            type="number"
                            onWheel={preventNumberInputScroll}
                            value={numericValue !== undefined && numericValue !== null ? numericValue : ''}
                            onChange={(e) => {
                              const input = e.target.value;
                              const value = input === '' ? undefined : Number(input);
                              const newTestData = { ...formData.testData, [fieldName]: value };
                              setFormData(prev => ({ ...prev, testData: newTestData }));

                              if (value !== undefined && !Number.isNaN(value) && value >= 0) {
                                calculateD2Percentile(condition.id as 'TR' | 'TA' | 'O' | 'C' | 'TOT' | 'CON' | 'TR+' | 'TR-' | 'VAR', value);
                              } else {
                                setD2Percentiles(prev => {
                                  const updated = { ...prev };
                                  delete updated[condition.id as keyof typeof updated];
                                  return updated;
                                });
                              }
                            }}
                            inputProps={{ min: 0, step: 1 }}
                            size="small"
                          />
                        </Box>

                        {(calculatingD2Percentile === condition.id || percentileData) && (
                          <Box mt={1} pl={2}>
                            {calculatingD2Percentile === condition.id ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <LoadingOrb size={20} />
                                <Typography variant="caption" color="text.secondary">Calculando percentil...</Typography>
                              </Box>
                            ) : percentileData ? (
                              <Box>
                                <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                                  <Chip
                                    label={`Percentil: ${percentileData.percentile || 'N/A'}`}
                                    color="info"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                  {percentileData.classifications?.length > 0 && (
                                    percentileData.classifications.length > 1 ? (
                                      <Chip
                                        label={`Clasificación: ${percentileData.classifications.join(' / ')}`}
                                        variant="filled"
                                        size="small"
                                        sx={{
                                          background: `linear-gradient(90deg, ${classificationColors[percentileData.classifications[0]] || '#757575'} 0%, ${classificationColors[percentileData.classifications[1]] || '#757575'} 100%)`,
                                          color: 'white',
                                          fontWeight: 'bold'
                                        }}
                                      />
                                    ) : (
                                      <Chip
                                        label={`Clasificación: ${percentileData.classifications[0]}`}
                                        variant="filled"
                                        size="small"
                                        sx={{
                                          bgcolor: classificationColors[percentileData.classifications[0]] || '#757575',
                                          color: 'white',
                                          fontWeight: 'bold'
                                        }}
                                      />
                                    )
                                  )}
                                </Box>
                                <Box mt={1}>
                                  {conversions.length > 0 ? conversions.slice(0, 2).map((conv: any, idx: number) => (
                                    <Typography key={`${condition.id}-conv-${idx}`} variant="caption" display="block" color="text.secondary">
                                      Escalar: {conv?.scaledScore?.average ?? '—'} | T: {conv?.tScore?.average ?? '—'} | Z: {conv?.zScore?.average ?? '—'} | CI: {conv?.standardScore?.average ?? '—'}
                                    </Typography>
                                  )) : (
                                    <Typography variant="caption" color="text.secondary">Conversión: —</Typography>
                                  )}
                                </Box>
                              </Box>
                            ) : null}
                          </Box>
                        )}
                      </Card>
                    );
                  })}
                </Box>

                <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                  <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'success.50', border: '2px solid', borderColor: 'success.main' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="success.dark">TOT</Typography>
                    <Typography variant="h5" fontWeight="bold">{formData.testData?.totScore ?? '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">TR - (O + C)</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>Percentil: {d2Percentiles.TOT?.percentile ?? '—'}</Typography>
                  </Card>
                  <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.15)' : 'warning.50', border: '2px solid', borderColor: 'warning.main' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">% Error</Typography>
                    <Typography variant="h5" fontWeight="bold">{formData.testData?.errorRate ?? '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">100 * (O + C) / TR</Typography>
                  </Card>
                  <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'info.50', border: '2px solid', borderColor: 'info.main' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="info.dark">Concentración</Typography>
                    <Typography variant="h5" fontWeight="bold">{formData.testData?.concentrationScore ?? '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">TA - C</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>Percentil: {d2Percentiles.CON?.percentile ?? '—'}</Typography>
                  </Card>
                </Box>

              </Box>
            )}

            {/* Campos para HVLT-R (4 intentos) */}
            {isHVLTR && (
              <Box mb={3}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Recuerdo Total:</strong> Se calcula automáticamente como la suma de los Ensayos 1, 2 y 3.
                    <br />
                    <strong>Recuerdo Tardío:</strong> Se registra en el Ensayo 4 (realizado 20-25 minutos después).
                  </Typography>
                </Alert>
                
                <HVLTRInput
                  ensayo1Words={hvltRWords.ensayo1}
                  ensayo2Words={hvltRWords.ensayo2}
                  ensayo3Words={hvltRWords.ensayo3}
                  ensayo4Words={hvltRWords.ensayo4}
                  onEnsayo1WordsChange={(words) => {
                    setHvltRWords(prev => ({ ...prev, ensayo1: words }));
                  }}
                  onEnsayo2WordsChange={(words) => {
                    setHvltRWords(prev => ({ ...prev, ensayo2: words }));
                  }}
                  onEnsayo3WordsChange={(words) => {
                    setHvltRWords(prev => ({ ...prev, ensayo3: words }));
                  }}
                  onEnsayo4WordsChange={(words) => {
                    setHvltRWords(prev => ({ ...prev, ensayo4: words }));
                  }}
                  onEnsayo1ScoreChange={(score) => {
                    const ensayo1 = score;
                    const ensayo2 = formData.testData?.ensayo2Score || 0;
                    const ensayo3 = formData.testData?.ensayo3Score || 0;
                    const recuerdoTotal = calculateHVLTRTotalRecall(ensayo1, ensayo2, ensayo3);
                    const newTestData = { 
                      ...formData.testData, 
                      ensayo1Score: score,
                      recuerdoTotalScore: recuerdoTotal
                    };
                    
                    if (recuerdoTotal > 0 && patientEducationYears && patientEducationYears > 0 && patientAge) {
                      calculateHVLTRPercentile('recuerdoTotal', recuerdoTotal);
                    }
                    
                    setFormData(prev => ({ ...prev, testData: newTestData }));
                  }}
                  onEnsayo2ScoreChange={(score) => {
                    const ensayo1 = formData.testData?.ensayo1Score || 0;
                    const ensayo2 = score;
                    const ensayo3 = formData.testData?.ensayo3Score || 0;
                    const recuerdoTotal = calculateHVLTRTotalRecall(ensayo1, ensayo2, ensayo3);
                    const newTestData = { 
                      ...formData.testData, 
                      ensayo2Score: score,
                      recuerdoTotalScore: recuerdoTotal
                    };
                    
                    if (recuerdoTotal > 0 && patientEducationYears && patientEducationYears > 0 && patientAge) {
                      calculateHVLTRPercentile('recuerdoTotal', recuerdoTotal);
                    }
                    
                    setFormData(prev => ({ ...prev, testData: newTestData }));
                  }}
                  onEnsayo3ScoreChange={(score) => {
                    const ensayo1 = formData.testData?.ensayo1Score || 0;
                    const ensayo2 = formData.testData?.ensayo2Score || 0;
                    const ensayo3 = score;
                    const recuerdoTotal = calculateHVLTRTotalRecall(ensayo1, ensayo2, ensayo3);
                    const newTestData = { 
                      ...formData.testData, 
                      ensayo3Score: score,
                      recuerdoTotalScore: recuerdoTotal
                    };
                    
                    if (recuerdoTotal > 0 && patientEducationYears && patientEducationYears > 0 && patientAge) {
                      calculateHVLTRPercentile('recuerdoTotal', recuerdoTotal);
                    }
                    
                    setFormData(prev => ({ ...prev, testData: newTestData }));
                  }}
                  onEnsayo4ScoreChange={(score) => {
                    const newTestData = { 
                      ...formData.testData, 
                      ensayo4Score: score,
                      recuerdoTardioScore: score // Ensayo 4 = Recuerdo Tardío
                    };
                    
                    if (score > 0 && patientEducationYears && patientEducationYears > 0 && patientAge) {
                      calculateHVLTRPercentile('recuerdoTardio', score);
                    }
                    
                    setFormData(prev => ({ ...prev, testData: newTestData }));
                  }}
                  ensayo1Score={formData.testData?.ensayo1Score || 0}
                  ensayo2Score={formData.testData?.ensayo2Score || 0}
                  ensayo3Score={formData.testData?.ensayo3Score || 0}
                  ensayo4Score={formData.testData?.ensayo4Score || 0}
                />
                  
                  {/* Mostrar Recuerdo Total y Recuerdo Tardío en la misma fila horizontal - Siempre visibles */}
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {/* Recuerdo Total - Siempre visible */}
                      <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'calc(50% - 8px)' } }}>
                        <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'success.50', border: '2px solid', borderColor: 'success.main', height: '100%' }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                                Recuerdo Total
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Suma automática: Ensayo 1 + Ensayo 2 + Ensayo 3
                              </Typography>
                            </Box>
                            <Chip
                              label={formData.testData?.recuerdoTotalScore || 0}
                              color="success"
                              sx={{ fontSize: '1.2rem', fontWeight: 'bold', px: 2, py: 2.5 }}
                            />
                          </Box>
                          
                          {/* Mostrar percentil de Recuerdo Total */}
                          {hvltRPercentiles.recuerdoTotal && (
                            <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                              {calculatingHVLTRPercentile === 'recuerdoTotal' ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                  <LoadingOrb size={20} />
                                  <Typography variant="caption" color="text.secondary">
                                    Calculando percentil...
                                  </Typography>
                                </Box>
                              ) : (
                                <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                                  <Chip
                                    label={`${hvltRPercentiles.recuerdoTotal?.percentile || 'N/A'}`}
                                    color="info"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                  {hvltRPercentiles.recuerdoTotal?.classifications && 
                                   hvltRPercentiles.recuerdoTotal.classifications.length > 0 && (
                                    hvltRPercentiles.recuerdoTotal.classifications.length > 1 ? (
                                      <Chip
                                        label={`Clasificación: ${hvltRPercentiles.recuerdoTotal.classifications.join(' / ')}`}
                                        variant="filled"
                                        size="small"
                                        sx={{
                                          background: `linear-gradient(90deg, ${classificationColors[hvltRPercentiles.recuerdoTotal.classifications[0]] || '#757575'} 0%, ${classificationColors[hvltRPercentiles.recuerdoTotal.classifications[1]] || '#757575'} 100%)`,
                                          color: 'white',
                                          fontWeight: 'bold'
                                        }}
                                      />
                                    ) : (
                                      <Chip
                                        label={`Clasificación: ${hvltRPercentiles.recuerdoTotal.classifications[0]}`}
                                        variant="filled"
                                        size="small"
                                        sx={{
                                          bgcolor: classificationColors[hvltRPercentiles.recuerdoTotal.classifications[0]] || '#757575',
                                          color: 'white',
                                          fontWeight: 'bold'
                                        }}
                                      />
                                    )
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}
                        </Card>
                      </Box>
                      
                      {/* Recuerdo Tardío - Siempre visible */}
                      <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'calc(50% - 8px)' } }}>
                        <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'info.50', border: '2px solid', borderColor: 'info.main', height: '100%' }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold" color="info.dark">
                                Recuerdo Tardío
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Ensayo 4 (20-25 minutos después)
                              </Typography>
                            </Box>
                            <Chip
                              label={formData.testData?.ensayo4Score || 0}
                              color="info"
                              sx={{ fontSize: '1.2rem', fontWeight: 'bold', px: 2, py: 2.5 }}
                            />
                          </Box>
                          
                          {/* Mostrar percentil de Recuerdo Tardío */}
                          {calculatingHVLTRPercentile === 'recuerdoTardio' ? (
                            <Box display="flex" alignItems="center" gap={1} mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                              <LoadingOrb size={20} />
                              <Typography variant="caption" color="text.secondary">
                                Calculando percentil...
                              </Typography>
                            </Box>
                          ) : hvltRPercentiles.recuerdoTardio ? (
                            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                              <Chip
                                label={`${hvltRPercentiles.recuerdoTardio?.percentile || 'N/A'}`}
                                color="info"
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                              />
                              {hvltRPercentiles.recuerdoTardio?.classifications && 
                               hvltRPercentiles.recuerdoTardio.classifications.length > 0 && (
                                hvltRPercentiles.recuerdoTardio.classifications.length > 1 ? (
                                  <Chip
                                    label={`Clasificación: ${hvltRPercentiles.recuerdoTardio.classifications.join(' / ')}`}
                                    variant="filled"
                                    size="small"
                                    sx={{
                                      background: `linear-gradient(90deg, ${classificationColors[hvltRPercentiles.recuerdoTardio.classifications[0]] || '#757575'} 0%, ${classificationColors[hvltRPercentiles.recuerdoTardio.classifications[1]] || '#757575'} 100%)`,
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    label={`Clasificación: ${hvltRPercentiles.recuerdoTardio.classifications[0]}`}
                                    variant="filled"
                                    size="small"
                                    sx={{
                                      bgcolor: classificationColors[hvltRPercentiles.recuerdoTardio.classifications[0]] || '#757575',
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                )
                              )}
                            </Box>
                          ) : null}
                        </Card>
                      </Box>
                    </Box>
                  </Box>
              </Box>
            )}

            {/* Campos para Rey Figure */}
            {isReyFigure && (
              <Box mb={3}>
                {/* Referencia: columna izq (tabla + inputs imágenes) 50% | columna der (figura Rey) 50% */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    mb: 3,
                    p: 2,
                    backgroundColor: (theme: { palette: { mode: string } }) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    alignItems: 'stretch',
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden'
                  }}
                >
                  {/* Columna izquierda: tabla + inputs de subida (Copia y Memoria) */}
                  <Box
                    sx={{
                      flex: { xs: 'none', md: '1 1 50%' },
                      width: { xs: '100%', md: '50%' },
                      minWidth: 0,
                      maxWidth: { md: '50%' },
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2
                    }}
                  >
                    <TableContainer component={Paper} sx={{ flexShrink: 0 }}>
                      <Table size="small" aria-label="Puntuación Figura de Rey" sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Puntuación</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Precisión</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Localización</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow><TableCell>2</TableCell><TableCell>Buena</TableCell><TableCell>Buena</TableCell></TableRow>
                          <TableRow><TableCell>1</TableCell><TableCell>Buena</TableCell><TableCell>Mala</TableCell></TableRow>
                          <TableRow><TableCell>1</TableCell><TableCell>Mala</TableCell><TableCell>Buena</TableCell></TableRow>
                          <TableRow><TableCell>0.5</TableCell><TableCell>Mala, reconocible</TableCell><TableCell>Mala</TableCell></TableRow>
                          <TableRow><TableCell>0</TableCell><TableCell>Mala, irreconocible</TableCell><TableCell>Mala</TableCell></TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {/* Inputs de subida de imágenes debajo de la tabla */}
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel shrink={!!reyFigureCopiaImage} sx={{ backgroundColor: 'background.paper', px: 0.5 }}>
                        Imagen de la Figura de Rey (Copia)
                      </InputLabel>
                      {reyFigureCopiaImage ? (
                        <OutlinedInput
                          readOnly
                          label="Imagen de la Figura de Rey (Copia)"
                          endAdornment={
                            <Box display="flex" gap={1}>
                              <Button size="small" variant="outlined" startIcon={<VisibilityIcon />}
                                onClick={() => reyFigureCopiaImagePreview && setImagePreviewModal({ open: true, imageUrl: reyFigureCopiaImagePreview, title: 'Imagen de la Figura de Rey (Copia)' })}
                              >Ver</Button>
                              <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                                onClick={() => {
                                  setReyFigureCopiaImage(null);
                                  setReyFigureCopiaImagePreview(null);
                                  const inp = document.getElementById('rey-figure-copia-image-upload') as HTMLInputElement; if (inp) inp.value = '';
                                }}
                              >Eliminar</Button>
                            </Box>
                          }
                          startAdornment={
                            <Box display="flex" alignItems="center" gap={1} mr={1}>
                              <CloudUploadIcon color="primary" />
                              <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0, maxWidth: '180px' }}>{reyFigureCopiaImage.name}</Typography>
                            </Box>
                          }
                          sx={{ minHeight: '48px' }}
                        />
                      ) : (
                        <>
                          <input accept="image/*" style={{ display: 'none' }} id="rey-figure-copia-image-upload" type="file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setReyFigureCopiaImage(file);
                                const reader = new FileReader();
                                reader.onloadend = () => setReyFigureCopiaImagePreview(reader.result as string);
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                          <OutlinedInput readOnly label="Imagen de la Figura de Rey (Copia)"
                            endAdornment={<label htmlFor="rey-figure-copia-image-upload"><IconButton component="span" color="primary"><CloudUploadIcon /></IconButton></label>}
                            onClick={() => (document.getElementById('rey-figure-copia-image-upload') as HTMLInputElement)?.click()}
                            sx={{ minHeight: '48px', cursor: 'pointer' }}
                          />
                        </>
                      )}
                    </FormControl>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel shrink={!!reyFigureMemoriaInmediataImage} sx={{ backgroundColor: 'background.paper', px: 0.5 }}>
                        Imagen Figura de Rey (Memoria inmediata)
                      </InputLabel>
                      {reyFigureMemoriaInmediataImage ? (
                        <OutlinedInput
                          readOnly
                          label="Imagen Figura de Rey (Memoria inmediata)"
                          endAdornment={
                            <Box display="flex" gap={1}>
                              <Button size="small" variant="outlined" startIcon={<VisibilityIcon />}
                                onClick={() => reyFigureMemoriaInmediataImagePreview && setImagePreviewModal({ open: true, imageUrl: reyFigureMemoriaInmediataImagePreview, title: 'Imagen Figura de Rey (Memoria inmediata)' })}
                              >Ver</Button>
                              <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                                onClick={() => {
                                  setReyFigureMemoriaInmediataImage(null);
                                  setReyFigureMemoriaInmediataImagePreview(null);
                                  const inp = document.getElementById('rey-figure-memoria-inmediata-image-upload') as HTMLInputElement; if (inp) inp.value = '';
                                }}
                              >Eliminar</Button>
                            </Box>
                          }
                          startAdornment={
                            <Box display="flex" alignItems="center" gap={1} mr={1}>
                              <CloudUploadIcon color="primary" />
                              <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0, maxWidth: '180px' }}>{reyFigureMemoriaInmediataImage.name}</Typography>
                            </Box>
                          }
                          sx={{ minHeight: '48px' }}
                        />
                      ) : (
                        <>
                          <input accept="image/*" style={{ display: 'none' }} id="rey-figure-memoria-inmediata-image-upload" type="file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setReyFigureMemoriaInmediataImage(file);
                                const reader = new FileReader();
                                reader.onloadend = () => setReyFigureMemoriaInmediataImagePreview(reader.result as string);
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                          <OutlinedInput readOnly label="Imagen Figura de Rey (Memoria inmediata)"
                            endAdornment={<label htmlFor="rey-figure-memoria-inmediata-image-upload"><IconButton component="span" color="primary"><CloudUploadIcon /></IconButton></label>}
                            onClick={() => (document.getElementById('rey-figure-memoria-inmediata-image-upload') as HTMLInputElement)?.click()}
                            sx={{ minHeight: '48px', cursor: 'pointer' }}
                          />
                        </>
                      )}
                    </FormControl>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel shrink={!!reyFigureMemoriaDiferidaImage} sx={{ backgroundColor: 'background.paper', px: 0.5 }}>
                        Imagen Figura de Rey (Memoria diferida)
                      </InputLabel>
                      {reyFigureMemoriaDiferidaImage ? (
                        <OutlinedInput
                          readOnly
                          label="Imagen Figura de Rey (Memoria diferida)"
                          endAdornment={
                            <Box display="flex" gap={1}>
                              <Button size="small" variant="outlined" startIcon={<VisibilityIcon />}
                                onClick={() => reyFigureMemoriaDiferidaImagePreview && setImagePreviewModal({ open: true, imageUrl: reyFigureMemoriaDiferidaImagePreview, title: 'Imagen Figura de Rey (Memoria diferida)' })}
                              >Ver</Button>
                              <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                                onClick={() => {
                                  setReyFigureMemoriaDiferidaImage(null);
                                  setReyFigureMemoriaDiferidaImagePreview(null);
                                  const inp = document.getElementById('rey-figure-memoria-diferida-image-upload') as HTMLInputElement; if (inp) inp.value = '';
                                }}
                              >Eliminar</Button>
                            </Box>
                          }
                          startAdornment={
                            <Box display="flex" alignItems="center" gap={1} mr={1}>
                              <CloudUploadIcon color="primary" />
                              <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0, maxWidth: '180px' }}>{reyFigureMemoriaDiferidaImage.name}</Typography>
                            </Box>
                          }
                          sx={{ minHeight: '48px' }}
                        />
                      ) : (
                        <>
                          <input accept="image/*" style={{ display: 'none' }} id="rey-figure-memoria-diferida-image-upload" type="file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setReyFigureMemoriaDiferidaImage(file);
                                const reader = new FileReader();
                                reader.onloadend = () => setReyFigureMemoriaDiferidaImagePreview(reader.result as string);
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                          <OutlinedInput readOnly label="Imagen Figura de Rey (Memoria diferida)"
                            endAdornment={<label htmlFor="rey-figure-memoria-diferida-image-upload"><IconButton component="span" color="primary"><CloudUploadIcon /></IconButton></label>}
                            onClick={() => (document.getElementById('rey-figure-memoria-diferida-image-upload') as HTMLInputElement)?.click()}
                            sx={{ minHeight: '48px', cursor: 'pointer' }}
                          />
                        </>
                      )}
                    </FormControl>
                  </Box>
                  {/* Columna derecha: figura de referencia (sin cambiar tamaño) */}
                  <Box
                    sx={{
                      flex: { xs: 'none', md: '1 1 50%' },
                      width: { xs: '100%', md: '50%' },
                      minWidth: 0,
                      maxWidth: { md: '50%' },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-start'
                    }}
                  >
                    <img
                      src="/images/rey-figure-reference.png"
                      alt="Figura compleja de Rey - Referencia para el evaluador"
                      style={{ maxWidth: '100%', width: '100%', height: 'auto', objectFit: 'contain', objectPosition: 'top' }}
                    />
                  </Box>
                </Box>

                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Registra los datos por elemento (0, 0.5, 1 o 2) o ingresa solo el puntaje total por columna:
                </Typography>

                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small" stickyHeader aria-label="Puntuación por elementos Rey" sx={{ tableLayout: 'fixed', minWidth: 520 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: '28%', maxWidth: 160 }}>Elementos</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '24%' }} align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            Copia
                            <Button size="small" variant="outlined" color="secondary" onClick={() => clearReyColumn('copia')} sx={{ minWidth: 'auto', py: 0.25, px: 0.75, fontSize: '0.7rem' }}>Limpiar</Button>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '24%' }} align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            M. Inmed.
                            <Button size="small" variant="outlined" color="secondary" onClick={() => clearReyColumn('memoriaInmediata')} sx={{ minWidth: 'auto', py: 0.25, px: 0.75, fontSize: '0.7rem' }}>Limpiar</Button>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '24%' }} align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            M. Diferida
                            <Button size="small" variant="outlined" color="secondary" onClick={() => clearReyColumn('memoriaDiferida')} sx={{ minWidth: 'auto', py: 0.25, px: 0.75, fontSize: '0.7rem' }}>Limpiar</Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {REY_ELEMENTOS.map((label, row) => (
                        <TableRow key={row}>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{row + 1}. {label}</TableCell>
                          {REY_COLS.map((col) => {
                            const key = `${col}-${row}`;
                            const value = reyFigureGrid[col][row];
                            const displayValue = value === '0.' ? '0.' : (value === '' ? '' : String(value));
                            return (
                              <TableCell key={key} align="center" sx={{ py: 0.25, width: '24%' }}>
                                <TextField
                                  id={`rey-score-${col}-${row}`}
                                  size="small"
                                  type="text"
                                  inputProps={{ maxLength: 3 }}
                                  value={displayValue}
                                  error={!!reyFigureCellErrors[key]}
                                  helperText={reyFigureCellErrors[key]}
                                  fullWidth
                                  sx={{ maxWidth: 88, '& .MuiFormHelperText-root': { margin: 0, fontSize: '0.65rem' } }}
                                  onClick={(e) => setReyFigureScoreMenu({ anchorEl: e.currentTarget, row, col })}
                                  onChange={(e) => {
                                    const raw = e.target.value.replace(',', '.');
                                    if (!isValidReyInput(raw)) {
                                      setReyFigureCellErrors(prev => ({ ...prev, [key]: 'Solo 0, 0.5, 1 o 2.' }));
                                      return;
                                    }
                                    setReyFigureCellErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
                                    const cellValue = reyInputToValue(raw);
                                    setReyFigureGrid(prev => ({ ...prev, [col]: [...prev[col].slice(0, row), cellValue, ...prev[col].slice(row + 1)] }));
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Tab' || e.key === 'Enter') {
                                      e.preventDefault();
                                      setReyFigureScoreMenu({ anchorEl: null, row: -1, col: 'copia' });
                                      if (e.shiftKey) {
                                        const prev = getPrevReyCell(row, col);
                                        if (prev) document.getElementById(`rey-score-${prev.col}-${prev.row}`)?.focus();
                                      } else {
                                        const next = getNextReyCell(row, col);
                                        if (next) document.getElementById(`rey-score-${next.col}-${next.row}`)?.focus();
                                      }
                                    }
                                  }}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>PUNTAJE TOTAL</TableCell>
                        <TableCell align="center" sx={{ width: '24%' }}>
                          <TextField
                            size="small"
                            type="number"
                            onWheel={preventNumberInputScroll}
                            inputProps={{ min: 0, step: 0.5 }}
                            InputProps={{ readOnly: reyFigureColumnHasAnyScore.copia }}
                            value={reyFigureColumnHasAnyScore.copia ? (reyFigureTotalCopia ?? reyFigurePartialSum.copia ?? '') : (reyFigureManualTotals.copia ?? reyFigureTotalCopia ?? '')}
                            onChange={(e) => {
                              if (reyFigureColumnHasAnyScore.copia) return;
                              const v = e.target.value === '' ? undefined : parseFloat(e.target.value);
                              setReyFigureManualTotals(prev => (v !== undefined ? { ...prev, copia: v } : { ...prev, copia: undefined }));
                            }}
                            fullWidth
                            sx={{ maxWidth: 88 }}
                            placeholder={reyFigureTotalCopia != null ? String(reyFigureTotalCopia) : ''}
                            helperText={reyFigureColumnHasAnyScore.copia ? 'Vacía los 18 cuadros para editar el total' : ''}
                            FormHelperTextProps={{ sx: { margin: 0, fontSize: '0.65rem' } }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ width: '24%' }}>
                          <TextField
                            size="small"
                            type="number"
                            onWheel={preventNumberInputScroll}
                            inputProps={{ min: 0, step: 0.5 }}
                            InputProps={{ readOnly: reyFigureColumnHasAnyScore.memoriaInmediata }}
                            value={reyFigureColumnHasAnyScore.memoriaInmediata ? (reyFigureTotalMemoriaInmediata ?? reyFigurePartialSum.memoriaInmediata ?? '') : (reyFigureManualTotals.memoriaInmediata ?? reyFigureTotalMemoriaInmediata ?? '')}
                            onChange={(e) => {
                              if (reyFigureColumnHasAnyScore.memoriaInmediata) return;
                              const v = e.target.value === '' ? undefined : parseFloat(e.target.value);
                              setReyFigureManualTotals(prev => (v !== undefined ? { ...prev, memoriaInmediata: v } : { ...prev, memoriaInmediata: undefined }));
                            }}
                            fullWidth
                            sx={{ maxWidth: 88 }}
                            placeholder={reyFigureTotalMemoriaInmediata != null ? String(reyFigureTotalMemoriaInmediata) : ''}
                            helperText={reyFigureColumnHasAnyScore.memoriaInmediata ? 'Vacía los 18 cuadros para editar el total' : ''}
                            FormHelperTextProps={{ sx: { margin: 0, fontSize: '0.65rem' } }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ width: '24%' }}>
                          <TextField
                            size="small"
                            type="number"
                            onWheel={preventNumberInputScroll}
                            inputProps={{ min: 0, step: 0.5 }}
                            InputProps={{ readOnly: reyFigureColumnHasAnyScore.memoriaDiferida }}
                            value={reyFigureColumnHasAnyScore.memoriaDiferida ? (reyFigureTotalMemoriaDiferida ?? reyFigurePartialSum.memoriaDiferida ?? '') : (reyFigureManualTotals.memoriaDiferida ?? reyFigureTotalMemoriaDiferida ?? '')}
                            onChange={(e) => {
                              if (reyFigureColumnHasAnyScore.memoriaDiferida) return;
                              const v = e.target.value === '' ? undefined : parseFloat(e.target.value);
                              setReyFigureManualTotals(prev => (v !== undefined ? { ...prev, memoriaDiferida: v } : { ...prev, memoriaDiferida: undefined }));
                            }}
                            fullWidth
                            sx={{ maxWidth: 88 }}
                            placeholder={reyFigureTotalMemoriaDiferida != null ? String(reyFigureTotalMemoriaDiferida) : ''}
                            helperText={reyFigureColumnHasAnyScore.memoriaDiferida ? 'Vacía los 18 cuadros para editar el total' : ''}
                            FormHelperTextProps={{ sx: { margin: 0, fontSize: '0.65rem' } }}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>TIEMPO TOTAL (min)</TableCell>
                        {REY_COLS.map((col) => (
                          <TableCell key={col} align="center" sx={{ width: '24%' }}>
                            <TextField
                              size="small"
                              type="number"
                              onWheel={preventNumberInputScroll}
                              inputProps={{ min: 0, step: 0.01 }}
                              value={reyFigureTiempos[col] === '' ? '' : reyFigureTiempos[col]}
                              onChange={(e) => {
                                const v = e.target.value === '' ? '' : parseFloat(e.target.value);
                                setReyFigureTiempos(prev => ({ ...prev, [col]: v }));
                              }}
                              fullWidth
                            sx={{ maxWidth: 88 }}
                            placeholder="min"
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>TIPO DE COPIA</TableCell>
                        <TableCell align="center">
                          <FormControl size="small" fullWidth>
                            <InputLabel>Tipo</InputLabel>
                            <Select
                              value={formData.testData?.copiaTipo || ''}
                              onChange={(e) => {
                                const value = e.target.value || undefined;
                                handleChange('testData', { ...formData.testData, copiaTipo: value });
                                if (value && patientAge) calculateReyFigurePercentile('memoriaTipoConstruccion', value);
                              }}
                              label="Tipo"
                            >
                              <MenuItem value="">No especificado</MenuItem>
                              <MenuItem value="I">I - Armazón</MenuItem>
                              <MenuItem value="II">II - Detalles en armazón</MenuItem>
                              <MenuItem value="III">III - Contorno general</MenuItem>
                              <MenuItem value="IV">IV - Yuxtaposición</MenuItem>
                              <MenuItem value="V">V - Fondo confuso</MenuItem>
                              <MenuItem value="VI">VI - Esquema familiar</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                        <TableCell align="center">—</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Menu
                  anchorEl={reyFigureScoreMenu.anchorEl}
                  open={!!reyFigureScoreMenu.anchorEl}
                  onClose={() => setReyFigureScoreMenu({ anchorEl: null, row: -1, col: 'copia' })}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  {REY_SCORES.map((s) => (
                    <MenuItem
                      key={s}
                      onClick={() => {
                        const { row, col } = reyFigureScoreMenu;
                        if (row >= 0 && col) {
                          setReyFigureGrid(prev => ({ ...prev, [col]: [...prev[col].slice(0, row), s, ...prev[col].slice(row + 1)] }));
                          setReyFigureCellErrors(prev => { const n = { ...prev }; delete n[`${col}-${row}`]; return n; });
                          setReyFigureScoreMenu({ anchorEl: null, row: -1, col: 'copia' });
                          const next = getNextReyCell(row, col);
                          if (next) setTimeout(() => document.getElementById(`rey-score-${next.col}-${next.row}`)?.focus(), 0);
                        }
                      }}
                    >
                      {s}
                    </MenuItem>
                  ))}
                </Menu>

                <Box sx={{ p: 2, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Resultados según baremos (percentil y calificación)
                  </Typography>
                  {calculatingReyFigurePercentile ? (
                    <Box display="flex" alignItems="center" gap={1}><LoadingOrb size={20} /><Typography variant="body2" color="text.secondary">Calculando...</Typography></Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Columna Copia */}
                      <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Copia</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                          {reyFigurePercentiles.copiaExactitud && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body2" color="text.secondary">Exactitud:</Typography>
                              <Chip size="small" variant="outlined" color="info" label={`P ${reyFigurePercentiles.copiaExactitud.percentile ?? 'N/A'}`} sx={{ fontWeight: 'bold' }} />
                              {reyFigurePercentiles.copiaExactitud.classifications?.length > 0 && (
                                reyFigurePercentiles.copiaExactitud.classifications.length > 1 ? (
                                  <Chip size="small" variant="filled" label={`${reyFigurePercentiles.copiaExactitud.classifications.join(' / ')}`} sx={{ background: `linear-gradient(90deg, ${classificationColors[reyFigurePercentiles.copiaExactitud.classifications[0]] || '#757575'} 0%, ${classificationColors[reyFigurePercentiles.copiaExactitud.classifications[1]] || '#757575'} 100%)`, color: 'white', fontWeight: 'bold' }} />
                                ) : (
                                  <Chip size="small" variant="filled" label={reyFigurePercentiles.copiaExactitud.classifications[0]} sx={{ bgcolor: classificationColors[reyFigurePercentiles.copiaExactitud.classifications[0]] || '#757575', color: 'white', fontWeight: 'bold' }} />
                                )
                              )}
                            </Box>
                          )}
                          {reyFigurePercentiles.copiaTiempo && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body2" color="text.secondary">Tiempo:</Typography>
                              <Chip size="small" variant="outlined" color="info" label={`P ${reyFigurePercentiles.copiaTiempo.percentile ?? 'N/A'}`} sx={{ fontWeight: 'bold' }} />
                              {reyFigurePercentiles.copiaTiempo.classifications?.length > 0 && (
                                reyFigurePercentiles.copiaTiempo.classifications.length > 1 ? (
                                  <Chip size="small" variant="filled" label={`${reyFigurePercentiles.copiaTiempo.classifications.join(' / ')}`} sx={{ background: `linear-gradient(90deg, ${classificationColors[reyFigurePercentiles.copiaTiempo.classifications[0]] || '#757575'} 0%, ${classificationColors[reyFigurePercentiles.copiaTiempo.classifications[1]] || '#757575'} 100%)`, color: 'white', fontWeight: 'bold' }} />
                                ) : (
                                  <Chip size="small" variant="filled" label={reyFigurePercentiles.copiaTiempo.classifications[0]} sx={{ bgcolor: classificationColors[reyFigurePercentiles.copiaTiempo.classifications[0]] || '#757575', color: 'white', fontWeight: 'bold' }} />
                                )
                              )}
                            </Box>
                          )}
                          {reyFigurePercentiles.memoriaTipoConstruccion && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body2" color="text.secondary">Tipo copia:</Typography>
                              <Chip size="small" variant="outlined" color="info" label={`P ${reyFigurePercentiles.memoriaTipoConstruccion.percentile ?? 'N/A'}`} sx={{ fontWeight: 'bold' }} />
                              {reyFigurePercentiles.memoriaTipoConstruccion.classifications?.length > 0 && (
                                reyFigurePercentiles.memoriaTipoConstruccion.classifications.length > 1 ? (
                                  <Chip size="small" variant="filled" label={`${reyFigurePercentiles.memoriaTipoConstruccion.classifications.join(' / ')}`} sx={{ background: `linear-gradient(90deg, ${classificationColors[reyFigurePercentiles.memoriaTipoConstruccion.classifications[0]] || '#757575'} 0%, ${classificationColors[reyFigurePercentiles.memoriaTipoConstruccion.classifications[1]] || '#757575'} 100%)`, color: 'white', fontWeight: 'bold' }} />
                                ) : (
                                  <Chip size="small" variant="filled" label={reyFigurePercentiles.memoriaTipoConstruccion.classifications[0]} sx={{ bgcolor: classificationColors[reyFigurePercentiles.memoriaTipoConstruccion.classifications[0]] || '#757575', color: 'white', fontWeight: 'bold' }} />
                                )
                              )}
                            </Box>
                          )}
                          {!reyFigurePercentiles.copiaExactitud && !reyFigurePercentiles.copiaTiempo && !reyFigurePercentiles.memoriaTipoConstruccion && (
                            <Typography variant="body2" color="text.secondary">Completa puntaje y/o tiempo de Copia.</Typography>
                          )}
                        </Box>
                      </Box>
                      {/* Columna Memoria inmediata (mismo baremo que Memoria diferida) */}
                      <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Memoria inmediata</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                          {reyFigurePercentiles.memoriaInmediata ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body2" color="text.secondary">Exactitud:</Typography>
                              <Chip size="small" variant="outlined" color="info" label={`P ${reyFigurePercentiles.memoriaInmediata.percentile ?? 'N/A'}`} sx={{ fontWeight: 'bold' }} />
                              {reyFigurePercentiles.memoriaInmediata.classifications?.length > 0 && (
                                reyFigurePercentiles.memoriaInmediata.classifications.length > 1 ? (
                                  <Chip size="small" variant="filled" label={`${reyFigurePercentiles.memoriaInmediata.classifications.join(' / ')}`} sx={{ background: `linear-gradient(90deg, ${classificationColors[reyFigurePercentiles.memoriaInmediata.classifications[0]] || '#757575'} 0%, ${classificationColors[reyFigurePercentiles.memoriaInmediata.classifications[1]] || '#757575'} 100%)`, color: 'white', fontWeight: 'bold' }} />
                                ) : (
                                  <Chip size="small" variant="filled" label={reyFigurePercentiles.memoriaInmediata.classifications[0]} sx={{ bgcolor: classificationColors[reyFigurePercentiles.memoriaInmediata.classifications[0]] || '#757575', color: 'white', fontWeight: 'bold' }} />
                                )
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Completa puntaje total de Memoria inmediata.</Typography>
                          )}
                          {reyFigureTiempos.memoriaInmediata !== '' && reyFigureTiempos.memoriaInmediata != null && (
                            <Typography variant="body2" color="text.secondary">Tiempo: {reyFigureTiempos.memoriaInmediata} min</Typography>
                          )}
                        </Box>
                      </Box>
                      {/* Columna Memoria diferida */}
                      <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Memoria diferida</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                          {reyFigurePercentiles.memoriaExactitud ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body2" color="text.secondary">Exactitud:</Typography>
                              <Chip size="small" variant="outlined" color="info" label={`P ${reyFigurePercentiles.memoriaExactitud.percentile ?? 'N/A'}`} sx={{ fontWeight: 'bold' }} />
                              {reyFigurePercentiles.memoriaExactitud.classifications?.length > 0 && (
                                reyFigurePercentiles.memoriaExactitud.classifications.length > 1 ? (
                                  <Chip size="small" variant="filled" label={`${reyFigurePercentiles.memoriaExactitud.classifications.join(' / ')}`} sx={{ background: `linear-gradient(90deg, ${classificationColors[reyFigurePercentiles.memoriaExactitud.classifications[0]] || '#757575'} 0%, ${classificationColors[reyFigurePercentiles.memoriaExactitud.classifications[1]] || '#757575'} 100%)`, color: 'white', fontWeight: 'bold' }} />
                                ) : (
                                  <Chip size="small" variant="filled" label={reyFigurePercentiles.memoriaExactitud.classifications[0]} sx={{ bgcolor: classificationColors[reyFigurePercentiles.memoriaExactitud.classifications[0]] || '#757575', color: 'white', fontWeight: 'bold' }} />
                                )
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Completa puntaje total de Memoria diferida.</Typography>
                          )}
                          {reyFigureTiempos.memoriaDiferida !== '' && reyFigureTiempos.memoriaDiferida != null && (
                            <Typography variant="body2" color="text.secondary">Tiempo: {reyFigureTiempos.memoriaDiferida} min</Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Campos para BNT (Boston Naming Test) */}
            {isBNT && (
              <Box mb={3}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Evalúa cada palabra marcando los criterios correspondientes:
                </Typography>
                
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <TableContainer component={Paper} sx={{ maxHeight: '600px', overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', position: 'sticky', left: 0, zIndex: 3, minWidth: '200px' }}>
                              Palabra
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: '250px' }}>
                              Clave Semántica
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: '120px' }}>
                              Respuesta Correcta
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: '120px' }}>
                              Clave Semántica
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: '120px' }}>
                              Clave Fonética
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: '150px' }}>
                              Parafasias Fonológica
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: '150px' }}>
                              Parafasias Verbales
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {BNT_WORD_LIST.map((bntWord, index) => {
                            const response = bntResponses[index] || {
                              respuestaCorrecta: false,
                              claveSemantica: false,
                              claveFonetica: false,
                              parafasiasFonologica: false,
                              parafasiasVerbales: false
                            };
                            const { phoneticKey, rest } = getPhoneticKeyParts(bntWord.word, bntWord.phoneticKeyLength);
                            
                            return (
                              <TableRow key={index}>
                                <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper', fontWeight: 'medium' }}>
                                  <Typography variant="body2">
                                    <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>{phoneticKey}</span>
                                    {rest}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {bntWord.semanticKey}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={response.respuestaCorrecta}
                                    onChange={(e) => {
                                      const newResponses = {
                                        ...bntResponses,
                                        [index]: {
                                          ...response,
                                          respuestaCorrecta: e.target.checked
                                        }
                                      };
                                      setBntResponses(newResponses);
                                      
                                      // Actualizar score total en formData
                                      const totalScore = Object.values(newResponses).filter(r => r.respuestaCorrecta).length;
                                      handleChange('testData', { ...formData.testData, bntScore: totalScore });
                                      
                                      // Calcular percentil si hay edad y escolaridad
                                      if (patientAge && patientEducationYears && patientEducationYears > 0) {
                                        calculateBntPercentile(totalScore);
                                      }
                                    }}
                                    color="primary"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={response.claveSemantica}
                                    onChange={(e) => {
                                      setBntResponses({
                                        ...bntResponses,
                                        [index]: {
                                          ...response,
                                          claveSemantica: e.target.checked
                                        }
                                      });
                                    }}
                                    color="primary"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={response.claveFonetica}
                                    onChange={(e) => {
                                      setBntResponses({
                                        ...bntResponses,
                                        [index]: {
                                          ...response,
                                          claveFonetica: e.target.checked
                                        }
                                      });
                                    }}
                                    color="primary"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={response.parafasiasFonologica}
                                    onChange={(e) => {
                                      setBntResponses({
                                        ...bntResponses,
                                        [index]: {
                                          ...response,
                                          parafasiasFonologica: e.target.checked
                                        }
                                      });
                                    }}
                                    color="primary"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={response.parafasiasVerbales}
                                    onChange={(e) => {
                                      setBntResponses({
                                        ...bntResponses,
                                        [index]: {
                                          ...response,
                                          parafasiasVerbales: e.target.checked
                                        }
                                      });
                                    }}
                                    color="primary"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>

                {/* Resumen de Score Total y Percentil */}
                <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'success.50', border: '2px solid', borderColor: 'success.main' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                        Score Total (Respuestas Correctas)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total: {calculateBntTotalScore()} / {BNT_WORD_LIST.length}
                      </Typography>
                    </Box>
                    <Chip
                      label={calculateBntTotalScore()}
                      color="success"
                      sx={{ fontSize: '1.2rem', fontWeight: 'bold', px: 2, py: 2.5 }}
                    />
                  </Box>
                  
                  {/* Mostrar percentil de BNT */}
                  {calculatingBntPercentile ? (
                    <Box display="flex" alignItems="center" gap={1} pt={2} borderTop="1px solid" borderColor="divider">
                      <LoadingOrb size={20} />
                      <Typography variant="caption" color="text.secondary">
                        Calculando percentil...
                      </Typography>
                    </Box>
                  ) : bntPercentile ? (
                    <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" pt={2} borderTop="1px solid" borderColor="divider">
                      <Chip
                        label={`Percentil: ${bntPercentile.percentile || 'N/A'}`}
                        color="info"
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 'bold' }}
                      />
                      {bntPercentile.classifications && bntPercentile.classifications.length > 0 && (
                        bntPercentile.classifications.length > 1 ? (
                          <Chip
                            label={`Clasificación: ${bntPercentile.classifications.join(' / ')}`}
                            variant="filled"
                            size="small"
                            sx={{
                              background: `linear-gradient(90deg, ${classificationColors[bntPercentile.classifications[0]] || '#757575'} 0%, ${classificationColors[bntPercentile.classifications[1]] || '#757575'} 100%)`,
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        ) : (
                          <Chip
                            label={`Clasificación: ${bntPercentile.classifications[0]}`}
                            variant="filled"
                            size="small"
                            sx={{
                              bgcolor: classificationColors[bntPercentile.classifications[0]] || '#757575',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        )
                      )}
                    </Box>
                  ) : calculateBntTotalScore() > 0 && patientAge && patientEducationYears && patientEducationYears > 0 ? (
                    <Box pt={2} borderTop="1px solid" borderColor="divider">
                      <Typography variant="caption" color="text.secondary">
                        El percentil se calculará automáticamente al guardar la prueba
                      </Typography>
                    </Box>
                  ) : null}
                </Card>
              </Box>
            )}

            {isElevator3B && (
              <Box mb={3}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Marca cada respuesta como correcta para calcular los aciertos de cada versión.
                </Typography>
                {([
                  {
                    key: 'versionA' as ElevatorVersionKey,
                    title: 'Subprueba 2: Conteo de Elevator (Versión A)',
                    rows: ELEVATOR_SUBTEST_A,
                    max: 7,
                  },
                  {
                    key: 'versionB' as ElevatorVersionKey,
                    title: 'Subprueba 3: Conteo de Elevator con Distracción (Versión B)',
                    rows: ELEVATOR_SUBTEST_B,
                    max: 10,
                  },
                ]).map((group) => {
                  const score = calculateElevatorTotalScore(group.key);
                  const percentileData = elevatorPercentiles[group.key];
                  return (
                    <Card key={group.key} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {group.title}
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: 72 }}>Tono</TableCell>
                                {group.rows[0].values.map((_, colIdx) => (
                                  <TableCell key={`${group.key}-head-${colIdx}`} align="center" sx={{ fontWeight: 'bold' }}>
                                    {colIdx + 1}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {group.rows.map((row, rowIdx) => (
                                <TableRow key={`${group.key}-${row.tone}`}>
                                  <TableCell sx={{ fontWeight: 'bold' }}>{row.tone}</TableCell>
                                  {row.values.map((value, colIdx) => {
                                    const responseIndex = rowIdx * row.values.length + colIdx;
                                    const checked = elevatorResponses[group.key][responseIndex] || false;
                                    return (
                                      <TableCell key={`${group.key}-${row.tone}-${colIdx}`} align="center">
                                        <Box display="flex" flexDirection="column" alignItems="center">
                                          <Typography variant="caption" color="text.secondary">{value}</Typography>
                                          <Checkbox
                                            size="small"
                                            checked={checked}
                                            onChange={(e) => {
                                              const next = [...elevatorResponses[group.key]];
                                              next[responseIndex] = e.target.checked;
                                              const nextResponses = { ...elevatorResponses, [group.key]: next };
                                              setElevatorResponses(nextResponses);
                                              const nextScore = next.filter(Boolean).length;
                                              calculateElevatorPercentile(group.key, nextScore);
                                            }}
                                          />
                                        </Box>
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Box mt={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                          <Chip label={`Aciertos: ${score}/${group.max}`} color="success" />
                          {calculatingElevatorPercentile === group.key ? (
                            <Chip label="Calculando percentil..." color="info" variant="outlined" />
                          ) : percentileData?.percentile ? (
                            <>
                              <Chip label={`Percentil: ${percentileData.percentile}`} color="info" variant="outlined" />
                              {percentileData.classifications?.length > 0 && (
                                <Chip
                                  label={`Clasificación: ${percentileData.classifications.join(' / ')}`}
                                  variant="filled"
                                  sx={{
                                    bgcolor: classificationColors[percentileData.classifications[0]] || '#757575',
                                    color: 'white',
                                    fontWeight: 'bold',
                                  }}
                                />
                              )}
                            </>
                          ) : null}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}

            <TextField
              fullWidth
              label="Observaciones"
              multiline
              rows={3}
              value={formData.observations ? String(formData.observations) : ''}
              onChange={(e) => handleChange('observations', e.target.value || '')}
              placeholder="Observaciones adicionales sobre la ejecución de la prueba..."
              helperText="Opcional: Notas sobre el comportamiento, dificultades, etc."
            />

            {/* Resumen para TMT */}
            {!isStroop && formData.testData?.timeSeconds && formData.testData.timeSeconds > 0 && (
              <Card sx={{ mt: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Resumen de Resultados:
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Chip 
                      icon={<TimerIcon />} 
                      label={`Tiempo: ${formatTime(formData.testData.timeSeconds)}`}
                      color="primary"
                    />
                    {formData.testData.errors !== undefined && formData.testData.errors !== null && (
                      <Chip 
                        icon={<ErrorIcon />} 
                        label={`Errores: ${formData.testData.errors}`}
                        color={formData.testData.errors > 0 ? 'error' : 'success'}
                      />
                    )}
                    {/* Percentil */}
                    {patientEducationYears && patientEducationYears > 0 && (
                      <Chip 
                        label={
                          calculatingPercentile 
                            ? 'Calculando percentil...' 
                            : (calculatedPercentile && calculatedPercentile.percentile !== null && calculatedPercentile.percentile !== undefined)
                              ? `Percentil: ${calculatedPercentile.percentile}` 
                              : 'Percentil: Se calculará automáticamente'
                        }
                        color="info"
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                        icon={calculatingPercentile ? <LoadingOrb size={20} /> : undefined}
                      />
                    )}
                    {/* Clasificación(es) */}
                    {calculatedPercentile && calculatedPercentile.classifications && calculatedPercentile.classifications.length > 0 && (
                      calculatedPercentile.classifications.length > 1 ? (
                        <Chip 
                          label={`Clasificación: ${calculatedPercentile.classifications.join(' / ')}`}
                          variant="filled"
                          sx={{
                            background: `linear-gradient(90deg, ${classificationColors[calculatedPercentile.classifications[0]] || '#757575'} 0%, ${classificationColors[calculatedPercentile.classifications[1]] || '#757575'} 100%)`,
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      ) : (
                        <Chip 
                          label={`Clasificación: ${calculatedPercentile.classifications[0]}`}
                          variant="filled"
                          sx={{
                            bgcolor: classificationColors[calculatedPercentile.classifications[0]] || '#757575',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      )
                    )}
                  </Box>
                  {patientEducationYears && patientEducationYears > 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {(calculatedPercentile && calculatedPercentile.percentile !== null && calculatedPercentile.percentile !== undefined)
                        ? `* Percentil calculado basado en los años de escolaridad del paciente (${patientEducationYears} años)${patientAge !== null ? ` y edad (${patientAge} años)` : ''}`
                        : `* El percentil se calculará automáticamente basado en los años de escolaridad del paciente (${patientEducationYears} años)${patientAge !== null ? ` y edad (${patientAge} años)` : ''}`
                      }
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                      ⚠️ Para calcular el percentil, es necesario configurar los años de escolaridad del paciente
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Resumen para MoCA */}
            {isMoca && formData.testData?.mocaScore !== undefined && formData.testData?.mocaScore !== null && (
              <Card sx={{ mt: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Resumen de Resultados:
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Chip
                      label={`Puntaje Directo: ${formData.testData.mocaScore} / 30`}
                      color="primary"
                    />
                    {patientAge !== null && (
                      <Chip
                        label={
                          calculatingMocaPercentile
                            ? 'Calculando percentil...'
                            : (mocaPercentile && mocaPercentile.percentile !== null && mocaPercentile.percentile !== undefined)
                              ? `Percentil: ${mocaPercentile.percentile}`
                              : 'Percentil: Se calculará automáticamente'
                        }
                        color="info"
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                        icon={calculatingMocaPercentile ? <LoadingOrb size={20} /> : undefined}
                      />
                    )}
                    {mocaPercentile && mocaPercentile.classifications && mocaPercentile.classifications.length > 0 && (
                      mocaPercentile.classifications.length > 1 ? (
                        <Chip
                          label={`Clasificación: ${mocaPercentile.classifications.join(' / ')}`}
                          variant="filled"
                          sx={{
                            background: `linear-gradient(90deg, ${classificationColors[mocaPercentile.classifications[0]] || '#757575'} 0%, ${classificationColors[mocaPercentile.classifications[1]] || '#757575'} 100%)`,
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      ) : (
                        <Chip
                          label={`Clasificación: ${mocaPercentile.classifications[0]}`}
                          variant="filled"
                          sx={{
                            bgcolor: classificationColors[mocaPercentile.classifications[0]] || '#757575',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      )
                    )}
                  </Box>
                  {patientAge !== null ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {`* Percentil calculado basado en la edad del paciente (${patientAge} años)`}
                      {patientEducationYears ? ` — Años de escolaridad: ${patientEducationYears} años` : ''}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                      ⚠️ Para calcular el percentil, es necesario configurar la fecha de nacimiento del paciente
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Resumen para IFS-Ch */}
            {isIFSCh && ifsChTotalScore !== undefined && (
              <Card sx={{ mt: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Resumen de Resultados:
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Chip label={`Puntaje Total: ${ifsChTotalScore} / 30`} color="primary" />
                    {ifsChWorkingMemoryIndex !== undefined && (
                      <Chip label={`Índice Memoria de Trabajo: ${ifsChWorkingMemoryIndex} / 10`} color="success" variant="outlined" />
                    )}
                    <Chip
                      label={
                        calculatingIfsChPercentile === 'total'
                          ? 'Calculando percentil...'
                          : (ifsChPercentiles.total && ifsChPercentiles.total.percentile !== null && ifsChPercentiles.total.percentile !== undefined)
                            ? `Percentil: ${ifsChPercentiles.total.percentile}`
                            : 'Percentil: Se calculará automáticamente'
                      }
                      color="info"
                      variant="filled"
                      sx={{ fontWeight: 'bold' }}
                      icon={calculatingIfsChPercentile === 'total' ? <LoadingOrb size={20} /> : undefined}
                    />
                    {ifsChPercentiles.total?.classifications && ifsChPercentiles.total.classifications.length > 0 && (
                      ifsChPercentiles.total.classifications.length > 1 ? (
                        <Chip
                          label={`Clasificación: ${ifsChPercentiles.total.classifications.join(' / ')}`}
                          variant="filled"
                          sx={{
                            background: `linear-gradient(90deg, ${classificationColors[ifsChPercentiles.total.classifications[0]] || '#757575'} 0%, ${classificationColors[ifsChPercentiles.total.classifications[1]] || '#757575'} 100%)`,
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      ) : (
                        <Chip
                          label={`Clasificación: ${ifsChPercentiles.total.classifications[0]}`}
                          variant="filled"
                          sx={{
                            bgcolor: classificationColors[ifsChPercentiles.total.classifications[0]] || '#757575',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      )
                    )}
                  </Box>
                  {renderMatrixComparisonBoxes(ifsChPercentiles.total?.matrices)}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    * Z y percentil calculados contra la media/DE de sujetos control de IFS-Ch (no usa edad ni escolaridad).
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Resumen para Stroop */}
            {isStroop && formData.testData?.palabraScore > 0 && formData.testData?.colorScore > 0 && formData.testData?.palabraColorScore > 0 && (
              <Card sx={{ mt: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Resumen de Resultados Stroop:
                  </Typography>
                  
                  {/* Mostrar resultados por condición con percentiles */}
                  <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
                    {/* Palabra */}
                    <Box p={1.5} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" fontWeight="medium">
                          Palabra:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="primary.main">
                          {formData.testData.palabraScore} ítems
                        </Typography>
                      </Box>
                      {stroopPercentiles.palabra && (
                        <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                          <Chip 
                            label={`Percentil: ${stroopPercentiles.palabra.percentile || 'N/A'}`} 
                            color="info" 
                            size="small"
                            variant="outlined"
                          />
                          {stroopPercentiles.palabra.classifications && stroopPercentiles.palabra.classifications.length > 0 && (
                            stroopPercentiles.palabra.classifications.length > 1 ? (
                              <Chip 
                                label={`Clasificación: ${stroopPercentiles.palabra.classifications.join(' / ')}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  background: `linear-gradient(90deg, ${classificationColors[stroopPercentiles.palabra.classifications[0]] || '#757575'} 0%, ${classificationColors[stroopPercentiles.palabra.classifications[1]] || '#757575'} 100%)`,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            ) : (
                              <Chip 
                                label={`Clasificación: ${stroopPercentiles.palabra.classifications[0]}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  bgcolor: classificationColors[stroopPercentiles.palabra.classifications[0]] || '#757575',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            )
                          )}
                        </Box>
                      )}
                    </Box>
                    
                    {/* Color */}
                    <Box p={1.5} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" fontWeight="medium">
                          Color:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="secondary.main">
                          {formData.testData.colorScore} ítems
                        </Typography>
                      </Box>
                      {stroopPercentiles.color && (
                        <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                          <Chip 
                            label={`Percentil: ${stroopPercentiles.color.percentile || 'N/A'}`} 
                            color="info" 
                            size="small"
                            variant="outlined"
                          />
                          {stroopPercentiles.color.classifications && stroopPercentiles.color.classifications.length > 0 && (
                            stroopPercentiles.color.classifications.length > 1 ? (
                              <Chip 
                                label={`Clasificación: ${stroopPercentiles.color.classifications.join(' / ')}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  background: `linear-gradient(90deg, ${classificationColors[stroopPercentiles.color.classifications[0]] || '#757575'} 0%, ${classificationColors[stroopPercentiles.color.classifications[1]] || '#757575'} 100%)`,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            ) : (
                              <Chip 
                                label={`Clasificación: ${stroopPercentiles.color.classifications[0]}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  bgcolor: classificationColors[stroopPercentiles.color.classifications[0]] || '#757575',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            )
                          )}
                        </Box>
                      )}
                    </Box>
                    
                    {/* Palabra-Color */}
                    <Box p={1.5} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" fontWeight="medium">
                          Palabra-Color:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="info.main">
                          {formData.testData.palabraColorScore} ítems
                        </Typography>
                      </Box>
                      {stroopPercentiles.palabraColor && (
                        <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                          <Chip 
                            label={`Percentil: ${stroopPercentiles.palabraColor.percentile || 'N/A'}`} 
                            color="info" 
                            size="small"
                            variant="outlined"
                          />
                          {stroopPercentiles.palabraColor.classifications && stroopPercentiles.palabraColor.classifications.length > 0 && (
                            stroopPercentiles.palabraColor.classifications.length > 1 ? (
                              <Chip 
                                label={`Clasificación: ${stroopPercentiles.palabraColor.classifications.join(' / ')}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  background: `linear-gradient(90deg, ${classificationColors[stroopPercentiles.palabraColor.classifications[0]] || '#757575'} 0%, ${classificationColors[stroopPercentiles.palabraColor.classifications[1]] || '#757575'} 100%)`,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            ) : (
                              <Chip 
                                label={`Clasificación: ${stroopPercentiles.palabraColor.classifications[0]}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  bgcolor: classificationColors[stroopPercentiles.palabraColor.classifications[0]] || '#757575',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            )
                          )}
                        </Box>
                      )}
                    </Box>
                    
                    {/* Interferencia */}
                    <Box p={1.5} sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? (formData.testData.interferenciaScore >= 0 ? 'rgba(76, 175, 80, 0.15)' : 'rgba(255, 152, 0, 0.15)') : (formData.testData.interferenciaScore >= 0 ? 'success.50' : 'warning.50'), borderRadius: 1, border: '2px solid', borderColor: formData.testData.interferenciaScore >= 0 ? 'success.main' : 'warning.main' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold" color={formData.testData.interferenciaScore >= 0 ? 'success.main' : 'warning.main'}>
                            Índice de Interferencia:
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Calculado automáticamente: PC' - PC
                          </Typography>
                        </Box>
                        <Typography variant="h6" fontWeight="bold" color={formData.testData.interferenciaScore >= 0 ? 'success.main' : 'warning.main'}>
                          {formData.testData.interferenciaScore?.toFixed(2) || '0.00'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    {patientEducationYears && patientEducationYears > 0 && patientAge
                      ? `* Percentiles calculados basados en los años de escolaridad del paciente (${patientEducationYears} años) y edad (${patientAge} años)`
                      : `* Los percentiles se calcularán automáticamente al guardar basados en los años de escolaridad y edad del paciente`
                    }
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Resumen para Fluencias */}
            {isFluencias && formData.testData?.animalsScore > 0 && formData.testData?.letterFScore > 0 && formData.testData?.letterAScore > 0 && formData.testData?.letterSScore > 0 && (
              <Card sx={{ mt: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Resumen de Resultados Fluencias:
                  </Typography>
                  
                  {/* Mostrar resultados por categoría con percentiles */}
                  <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
                    {/* Animals */}
                    <Box p={1.5} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" fontWeight="medium">
                          Animals:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="primary.main">
                          {formData.testData.animalsScore} palabras
                        </Typography>
                      </Box>
                      {fluenciasPercentiles.animals && (
                        <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                          <Chip 
                            label={`Percentil: ${fluenciasPercentiles.animals.percentile || 'N/A'}`} 
                            color="info" 
                            size="small"
                            variant="outlined"
                          />
                          {fluenciasPercentiles.animals.classifications && fluenciasPercentiles.animals.classifications.length > 0 && (
                            fluenciasPercentiles.animals.classifications.length > 1 ? (
                              <Chip 
                                label={`Clasificación: ${fluenciasPercentiles.animals.classifications.join(' / ')}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  background: `linear-gradient(90deg, ${classificationColors[fluenciasPercentiles.animals.classifications[0]] || '#757575'} 0%, ${classificationColors[fluenciasPercentiles.animals.classifications[1]] || '#757575'} 100%)`,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            ) : (
                              <Chip 
                                label={`Clasificación: ${fluenciasPercentiles.animals.classifications[0]}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  bgcolor: classificationColors[fluenciasPercentiles.animals.classifications[0]] || '#757575',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            )
                          )}
                        </Box>
                      )}
                    </Box>
                    
                    {/* Letter F */}
                    <Box p={1.5} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" fontWeight="medium">
                          Letra F:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="secondary.main">
                          {formData.testData.letterFScore} palabras
                        </Typography>
                      </Box>
                      {fluenciasPercentiles.letterF && (
                        <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                          <Chip 
                            label={`Percentil: ${fluenciasPercentiles.letterF.percentile || 'N/A'}`} 
                            color="info" 
                            size="small"
                            variant="outlined"
                          />
                          {fluenciasPercentiles.letterF.classifications && fluenciasPercentiles.letterF.classifications.length > 0 && (
                            fluenciasPercentiles.letterF.classifications.length > 1 ? (
                              <Chip 
                                label={`Clasificación: ${fluenciasPercentiles.letterF.classifications.join(' / ')}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  background: `linear-gradient(90deg, ${classificationColors[fluenciasPercentiles.letterF.classifications[0]] || '#757575'} 0%, ${classificationColors[fluenciasPercentiles.letterF.classifications[1]] || '#757575'} 100%)`,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            ) : (
                              <Chip 
                                label={`Clasificación: ${fluenciasPercentiles.letterF.classifications[0]}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  bgcolor: classificationColors[fluenciasPercentiles.letterF.classifications[0]] || '#757575',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            )
                          )}
                        </Box>
                      )}
                    </Box>
                    
                    {/* Letter A */}
                    <Box p={1.5} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" fontWeight="medium">
                          Letra A:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="info.main">
                          {formData.testData.letterAScore} palabras
                        </Typography>
                      </Box>
                      {fluenciasPercentiles.letterA && (
                        <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                          <Chip 
                            label={`Percentil: ${fluenciasPercentiles.letterA.percentile || 'N/A'}`} 
                            color="info" 
                            size="small"
                            variant="outlined"
                          />
                          {fluenciasPercentiles.letterA.classifications && fluenciasPercentiles.letterA.classifications.length > 0 && (
                            fluenciasPercentiles.letterA.classifications.length > 1 ? (
                              <Chip 
                                label={`Clasificación: ${fluenciasPercentiles.letterA.classifications.join(' / ')}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  background: `linear-gradient(90deg, ${classificationColors[fluenciasPercentiles.letterA.classifications[0]] || '#757575'} 0%, ${classificationColors[fluenciasPercentiles.letterA.classifications[1]] || '#757575'} 100%)`,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            ) : (
                              <Chip 
                                label={`Clasificación: ${fluenciasPercentiles.letterA.classifications[0]}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  bgcolor: classificationColors[fluenciasPercentiles.letterA.classifications[0]] || '#757575',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            )
                          )}
                        </Box>
                      )}
                    </Box>
                    
                    {/* Letter S */}
                    <Box p={1.5} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" fontWeight="medium">
                          Letra S:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="warning.main">
                          {formData.testData.letterSScore} palabras
                        </Typography>
                      </Box>
                      {fluenciasPercentiles.letterS && (
                        <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                          <Chip 
                            label={`Percentil: ${fluenciasPercentiles.letterS.percentile || 'N/A'}`} 
                            color="info" 
                            size="small"
                            variant="outlined"
                          />
                          {fluenciasPercentiles.letterS.classifications && fluenciasPercentiles.letterS.classifications.length > 0 && (
                            fluenciasPercentiles.letterS.classifications.length > 1 ? (
                              <Chip 
                                label={`Clasificación: ${fluenciasPercentiles.letterS.classifications.join(' / ')}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  background: `linear-gradient(90deg, ${classificationColors[fluenciasPercentiles.letterS.classifications[0]] || '#757575'} 0%, ${classificationColors[fluenciasPercentiles.letterS.classifications[1]] || '#757575'} 100%)`,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            ) : (
                              <Chip 
                                label={`Clasificación: ${fluenciasPercentiles.letterS.classifications[0]}`}
                                size="small"
                                variant="filled"
                                sx={{
                                  bgcolor: classificationColors[fluenciasPercentiles.letterS.classifications[0]] || '#757575',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            )
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    {patientEducationYears && patientEducationYears > 0 && patientAge
                      ? `* Percentiles calculados basados en los años de escolaridad del paciente (${patientEducationYears} años) y edad (${patientAge} años)`
                      : `* Los percentiles se calcularán automáticamente al guardar basados en los años de escolaridad y edad del paciente`
                    }
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        );
      }

      case 2: {
        const isStroopConfirm = formData.testTypeCode === 'STROOP';
        const isFluenciasConfirm = formData.testTypeCode === 'FLUENCIAS';
        const isHVLTRConfirm = formData.testTypeCode === 'HVLT-R';
        const isReyFigureConfirm = formData.testTypeCode === 'REY-FIGURE';
        const isBNTConfirm = formData.testTypeCode === 'BNT';
        const isD2Confirm = formData.testTypeCode === 'D2';
        const isElevator3BConfirm = formData.testTypeCode === 'ELEVATOR-3B';
        const isMocaConfirm = formData.testTypeCode === 'MOCA';
        const isIFSChConfirm = formData.testTypeCode === 'IFS-CH';

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirmar Datos
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Revisa los datos antes de guardar la prueba
            </Typography>
            
            <Card>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" gutterBottom>
                  Información de la Prueba
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {/* Info básica - solo si no es Rey Figure ni BNT */}
                {!isReyFigureConfirm && !isBNTConfirm && !isElevator3BConfirm && (
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Paciente
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {patientName}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Tipo de Prueba
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formData.testTypeCode && TEST_CONFIGS[formData.testTypeCode as keyof typeof TEST_CONFIGS]?.name}
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                {/* Info básica - solo para Rey Figure y BNT (sin grid) */}
                {(isReyFigureConfirm || isBNTConfirm || isElevator3BConfirm) && (
                  <Box display="flex" gap={4} mb={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Paciente
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {patientName}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Tipo de Prueba
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formData.testTypeCode && TEST_CONFIGS[formData.testTypeCode as keyof typeof TEST_CONFIGS]?.name}
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                {!isReyFigureConfirm && !isBNTConfirm && !isElevator3BConfirm && (
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    {/* Datos TMT */}
                    {!isStroopConfirm && !isFluenciasConfirm && !isHVLTRConfirm && !isD2Confirm && !isMocaConfirm && !isIFSChConfirm && (
                    <>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Tiempo
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formData.testData?.timeSeconds ? formatTime(formData.testData.timeSeconds) : 'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Errores
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formData.testData?.errors || 0}
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {/* Datos Stroop */}
                  {isStroopConfirm && (
                    <>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Palabra (ítems)
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formData.testData?.palabraScore || 0}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Color (ítems)
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formData.testData?.colorScore || 0}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Palabra-Color (ítems)
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formData.testData?.palabraColorScore || 0}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Interferencia
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color={formData.testData?.interferenciaScore >= 0 ? 'success.main' : 'warning.main'}>
                          {formData.testData?.interferenciaScore?.toFixed(2) || '0.00'}
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  {/* Percentiles y clasificaciones para TMT */}
                  {!isStroopConfirm && !isFluenciasConfirm && calculatedPercentile && calculatedPercentile.percentile && (
                    <>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Percentil
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="info.main">
                          {calculatedPercentile.percentile}
                        </Typography>
                      </Box>
                      
                      {calculatedPercentile.classifications && calculatedPercentile.classifications.length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Clasificación
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {calculatedPercentile.classifications.join(' / ')}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                  
                  {/* Percentiles y clasificaciones para Stroop */}
                  {isStroopConfirm && (
                    <>
                      {stroopPercentiles.palabra && stroopPercentiles.palabra.percentile && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Palabra - Percentil
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" color="info.main">
                            {stroopPercentiles.palabra.percentile}
                            {stroopPercentiles.palabra.classifications && stroopPercentiles.palabra.classifications.length > 0 && (
                              <Chip 
                                label={stroopPercentiles.palabra.classifications.join(' / ')}
                                size="small"
                                sx={{ ml: 1 }}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Typography>
                        </Box>
                      )}
                      
                      {stroopPercentiles.color && stroopPercentiles.color.percentile && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Color - Percentil
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" color="info.main">
                            {stroopPercentiles.color.percentile}
                            {stroopPercentiles.color.classifications && stroopPercentiles.color.classifications.length > 0 && (
                              <Chip 
                                label={stroopPercentiles.color.classifications.join(' / ')}
                                size="small"
                                sx={{ ml: 1 }}
                                color="secondary"
                                variant="outlined"
                              />
                            )}
                          </Typography>
                        </Box>
                      )}
                      
                      {stroopPercentiles.palabraColor && stroopPercentiles.palabraColor.percentile && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Palabra-Color - Percentil
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" color="info.main">
                            {stroopPercentiles.palabraColor.percentile}
                            {stroopPercentiles.palabraColor.classifications && stroopPercentiles.palabraColor.classifications.length > 0 && (
                              <Chip 
                                label={stroopPercentiles.palabraColor.classifications.join(' / ')}
                                size="small"
                                sx={{ ml: 1 }}
                                color="info"
                                variant="outlined"
                              />
                            )}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}

                  {/* Percentiles y clasificaciones para Fluencias */}
                  {isFluenciasConfirm && (
                    <>
                      {fluenciasPercentiles.animals && fluenciasPercentiles.animals.percentile && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Animals - Percentil
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" color="info.main">
                            {fluenciasPercentiles.animals.percentile}
                            {fluenciasPercentiles.animals.classifications && fluenciasPercentiles.animals.classifications.length > 0 && (
                              <Chip 
                                label={fluenciasPercentiles.animals.classifications.join(' / ')}
                                size="small"
                                sx={{ ml: 1 }}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Typography>
                        </Box>
                      )}
                      
                      {fluenciasPercentiles.letterF && fluenciasPercentiles.letterF.percentile && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Letra F - Percentil
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" color="info.main">
                            {fluenciasPercentiles.letterF.percentile}
                            {fluenciasPercentiles.letterF.classifications && fluenciasPercentiles.letterF.classifications.length > 0 && (
                              <Chip 
                                label={fluenciasPercentiles.letterF.classifications.join(' / ')}
                                size="small"
                                sx={{ ml: 1 }}
                                color="secondary"
                                variant="outlined"
                              />
                            )}
                          </Typography>
                        </Box>
                      )}
                      
                      {fluenciasPercentiles.letterA && fluenciasPercentiles.letterA.percentile && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Letra A - Percentil
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" color="info.main">
                            {fluenciasPercentiles.letterA.percentile}
                            {fluenciasPercentiles.letterA.classifications && fluenciasPercentiles.letterA.classifications.length > 0 && (
                              <Chip 
                                label={fluenciasPercentiles.letterA.classifications.join(' / ')}
                                size="small"
                                sx={{ ml: 1 }}
                                color="info"
                                variant="outlined"
                              />
                            )}
                          </Typography>
                        </Box>
                      )}
                      
                      {fluenciasPercentiles.letterS && fluenciasPercentiles.letterS.percentile && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Letra S - Percentil
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" color="info.main">
                            {fluenciasPercentiles.letterS.percentile}
                            {fluenciasPercentiles.letterS.classifications && fluenciasPercentiles.letterS.classifications.length > 0 && (
                              <Chip 
                                label={fluenciasPercentiles.letterS.classifications.join(' / ')}
                                size="small"
                                sx={{ ml: 1 }}
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                  
                  {isHVLTRConfirm && (
                    <>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Ensayo 1
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {formData.testData?.ensayo1Score || 0} / 12 palabras
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Ensayo 2
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {formData.testData?.ensayo2Score || 0} / 12 palabras
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Ensayo 3
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {formData.testData?.ensayo3Score || 0} / 12 palabras
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Ensayo 4 (Recuerdo Tardío)
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {formData.testData?.ensayo4Score || 0} / 12 palabras
                        </Typography>
                      </Box>
                      {/* Recuerdo Total y Recuerdo Tardío horizontalmente */}
                      <Box sx={{ gridColumn: '1 / -1', pt: 2, borderTop: '2px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          {/* Recuerdo Total - Siempre visible */}
                          <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Recuerdo Total (Calculado automáticamente)
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                              {formData.testData?.recuerdoTotalScore || 0} palabras
                            </Typography>
                            {hvltRPercentiles.recuerdoTotal ? (
                              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                                <Chip 
                                  label={hvltRPercentiles.recuerdoTotal.isRange 
                                    ? `${hvltRPercentiles.recuerdoTotal.percentile}` 
                                    : `${hvltRPercentiles.recuerdoTotal.percentile || 'N/A'}`} 
                                  color="info" 
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                {hvltRPercentiles.recuerdoTotal.classifications && hvltRPercentiles.recuerdoTotal.classifications.length > 0 && (
                                  hvltRPercentiles.recuerdoTotal.classifications.length > 1 ? (
                                    <Chip 
                                      label={`Clasificación: ${hvltRPercentiles.recuerdoTotal.classifications.join(' / ')}`}
                                      size="small"
                                      variant="filled"
                                      sx={{
                                        background: `linear-gradient(90deg, ${classificationColors[hvltRPercentiles.recuerdoTotal.classifications[0]] || '#757575'} 0%, ${classificationColors[hvltRPercentiles.recuerdoTotal.classifications[1]] || '#757575'} 100%)`,
                                        color: 'white',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  ) : (
                                    <Chip 
                                      label={`Clasificación: ${hvltRPercentiles.recuerdoTotal.classifications[0]}`}
                                      size="small"
                                      variant="filled"
                                      sx={{
                                        bgcolor: classificationColors[hvltRPercentiles.recuerdoTotal.classifications[0]] || '#757575',
                                        color: 'white',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  )
                                )}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Percentil no calculado
                              </Typography>
                            )}
                          </Box>
                          
                          {/* Recuerdo Tardío - Siempre visible */}
                          <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Recuerdo Tardío
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="info.main">
                              {formData.testData?.ensayo4Score || 0} palabras
                            </Typography>
                            {hvltRPercentiles.recuerdoTardio ? (
                              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                                <Chip 
                                  label={hvltRPercentiles.recuerdoTardio.isRange 
                                    ? `${hvltRPercentiles.recuerdoTardio.percentile}` 
                                    : `${hvltRPercentiles.recuerdoTardio.percentile || 'N/A'}`} 
                                  color="info" 
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                {hvltRPercentiles.recuerdoTardio.classifications && hvltRPercentiles.recuerdoTardio.classifications.length > 0 && (
                                  hvltRPercentiles.recuerdoTardio.classifications.length > 1 ? (
                                    <Chip 
                                      label={`Clasificación: ${hvltRPercentiles.recuerdoTardio.classifications.join(' / ')}`}
                                      size="small"
                                      variant="filled"
                                      sx={{
                                        background: `linear-gradient(90deg, ${classificationColors[hvltRPercentiles.recuerdoTardio.classifications[0]] || '#757575'} 0%, ${classificationColors[hvltRPercentiles.recuerdoTardio.classifications[1]] || '#757575'} 100%)`,
                                        color: 'white',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  ) : (
                                    <Chip 
                                      label={`Clasificación: ${hvltRPercentiles.recuerdoTardio.classifications[0]}`}
                                      size="small"
                                      variant="filled"
                                      sx={{
                                        bgcolor: classificationColors[hvltRPercentiles.recuerdoTardio.classifications[0]] || '#757575',
                                        color: 'white',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  )
                                )}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Percentil no calculado
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}
                  </Box>
                )}

                {/* Datos Rey Figure — Paso 3: 1) 3 imágenes en fila (clic para agrandar), 2) tabla, 3) percentiles y clasificaciones */}
                {isReyFigureConfirm && (
                    <>
                      {/* 1. Tres imágenes en fila, clic para agrandar */}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 3 }}>
                        {[
                          { preview: reyFigureCopiaImagePreview, title: 'Copia', alt: 'Figura de Rey - Copia' },
                          { preview: reyFigureMemoriaInmediataImagePreview, title: 'Memoria inmediata', alt: 'Figura de Rey - Memoria inmediata' },
                          { preview: reyFigureMemoriaDiferidaImagePreview, title: 'Memoria diferida', alt: 'Figura de Rey - Memoria diferida' }
                        ].map(({ preview, title, alt }) => (
                          <Box key={title} sx={{ flex: '1 1 0', minWidth: 160, maxWidth: 320 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom align="center">
                              {title}
                            </Typography>
                            {preview ? (
                              <Box
                                component="img"
                                src={preview}
                                alt={alt}
                                onClick={() => setImagePreviewModal({ open: true, imageUrl: preview, title: `Figura de Rey (${title})` })}
                                sx={{
                                  width: '100%',
                                  maxHeight: 220,
                                  objectFit: 'contain',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  cursor: 'pointer',
                                  '&:hover': { boxShadow: 2, borderColor: 'primary.main' }
                                }}
                              />
                            ) : (
                              <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                                <Typography variant="caption" color="text.secondary">Sin imagen</Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>

                      <Divider sx={{ my: 2, mx: 2 }} />

                      {/* 2. Tabla de datos (solo lectura) */}
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ px: 2 }}>
                        Tabla de puntuación
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, mx: 0 }}>
                        <Table size="small" sx={{ tableLayout: 'fixed' }}>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200' }}>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Elementos</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', width: '22%' }}>Copia</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', width: '22%' }}>M. Inmed.</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', width: '22%' }}>M. Diferida</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {REY_ELEMENTOS.map((label, row) => (
                              <TableRow key={row}>
                                <TableCell sx={{ fontSize: '0.7rem', py: 0.4 }}>{row + 1}. {label}</TableCell>
                                {REY_COLS.map((col) => {
                                  const value = reyFigureGrid[col][row];
                                  const display = value === '' || value === '0.' ? '—' : String(value);
                                  return <TableCell key={`${col}-${row}`} align="center" sx={{ py: 0.3, fontSize: '0.8rem' }}>{display}</TableCell>;
                                })}
                              </TableRow>
                            ))}
                            <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>PUNTAJE TOTAL</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{reyFigureColumnHasAnyScore.copia ? (reyFigureTotalCopia ?? reyFigurePartialSum.copia ?? '—') : (reyFigureManualTotals.copia ?? reyFigureTotalCopia ?? '—')}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{reyFigureColumnHasAnyScore.memoriaInmediata ? (reyFigureTotalMemoriaInmediata ?? reyFigurePartialSum.memoriaInmediata ?? '—') : (reyFigureManualTotals.memoriaInmediata ?? reyFigureTotalMemoriaInmediata ?? '—')}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{reyFigureColumnHasAnyScore.memoriaDiferida ? (reyFigureTotalMemoriaDiferida ?? reyFigurePartialSum.memoriaDiferida ?? '—') : (reyFigureManualTotals.memoriaDiferida ?? reyFigureTotalMemoriaDiferida ?? '—')}</TableCell>
                            </TableRow>
                            <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>TIEMPO TOTAL (min)</TableCell>
                              {REY_COLS.map((col) => (
                                <TableCell key={col} align="center" sx={{ fontSize: '0.8rem' }}>{reyFigureTiempos[col] === '' ? '—' : reyFigureTiempos[col]}</TableCell>
                              ))}
                            </TableRow>
                            <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>TIPO DE COPIA</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{formData.testData?.copiaTipo ?? '—'}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>—</TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.8rem' }}>—</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Divider sx={{ my: 2, mx: 2 }} />

                      {/* 3. Resultados: percentiles y clasificaciones — Puntaje, Percentil y Calificación uno debajo del otro */}
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ px: 2 }}>
                        Resultados según baremos
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'stretch', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mx: 0 }}>
                        <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, px: 2, py: 2, borderRight: { xs: 'none', md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">Exactitud Copia</Typography>
                          <Typography variant="body2">Puntaje: {formData.testData?.copiaExactitud ?? 'N/A'}</Typography>
                          <Typography variant="body2">Percentil: {reyFigurePercentiles.copiaExactitud?.percentile ?? 'N/A'}</Typography>
                          {(reyFigurePercentiles.copiaExactitud?.classifications?.length ?? 0) > 0 ? (
                            <>
                              <Typography variant="body2">Calificación:</Typography>
                              <Chip size="small" variant="filled" label={reyFigurePercentiles.copiaExactitud!.classifications!.join(' / ')} sx={{ bgcolor: classificationColors[reyFigurePercentiles.copiaExactitud!.classifications![0]] || '#757575', color: 'white', fontWeight: 'bold' }} />
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Calificación: —</Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, px: 2, py: 2, borderRight: { xs: 'none', md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">Tiempo Copia</Typography>
                          <Typography variant="body2">Puntaje: {formData.testData?.copiaTiempo ?? 'N/A'} min</Typography>
                          <Typography variant="body2">Percentil: {reyFigurePercentiles.copiaTiempo?.percentile ?? 'N/A'}</Typography>
                          {(reyFigurePercentiles.copiaTiempo?.classifications?.length ?? 0) > 0 ? (
                            <>
                              <Typography variant="body2">Calificación:</Typography>
                              <Chip size="small" variant="filled" label={reyFigurePercentiles.copiaTiempo!.classifications!.join(' / ')} sx={{ bgcolor: classificationColors[reyFigurePercentiles.copiaTiempo!.classifications![0]] || '#757575', color: 'white', fontWeight: 'bold' }} />
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Calificación: —</Typography>
                          )}
                        </Box>
                        {formData.testData?.copiaTipo && (
                          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, px: 2, py: 2, borderRight: { xs: 'none', md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="bold">Tipo copia</Typography>
                            <Typography variant="body2">Puntaje: {formData.testData.copiaTipo}</Typography>
                            <Typography variant="body2">Percentil: {reyFigurePercentiles.memoriaTipoConstruccion?.percentile ?? 'N/A'}</Typography>
                            {(reyFigurePercentiles.memoriaTipoConstruccion?.classifications?.length ?? 0) > 0 ? (
                              <>
                                <Typography variant="body2">Calificación:</Typography>
                                <Chip size="small" variant="filled" label={reyFigurePercentiles.memoriaTipoConstruccion!.classifications!.join(' / ')} sx={{ bgcolor: classificationColors[reyFigurePercentiles.memoriaTipoConstruccion!.classifications![0]] || '#757575', color: 'white', fontWeight: 'bold' }} />
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">Calificación: —</Typography>
                            )}
                          </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, px: 2, py: 2, borderRight: { xs: 'none', md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">Memoria inmediata</Typography>
                          <Typography variant="body2">Puntaje: {reyFigureManualTotals.memoriaInmediata ?? reyFigureTotalMemoriaInmediata ?? 'N/A'}</Typography>
                          <Typography variant="body2">Percentil: {reyFigurePercentiles.memoriaInmediata?.percentile ?? 'N/A'}</Typography>
                          {(reyFigurePercentiles.memoriaInmediata?.classifications?.length ?? 0) > 0 ? (
                            <>
                              <Typography variant="body2">Calificación:</Typography>
                              <Chip size="small" variant="filled" label={reyFigurePercentiles.memoriaInmediata!.classifications!.join(' / ')} sx={{ bgcolor: classificationColors[reyFigurePercentiles.memoriaInmediata!.classifications![0]] || '#757575', color: 'white', fontWeight: 'bold' }} />
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Calificación: —</Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, px: 2, py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">Memoria diferida</Typography>
                          <Typography variant="body2">Puntaje: {formData.testData?.memoriaExactitud ?? reyFigureManualTotals.memoriaDiferida ?? reyFigureTotalMemoriaDiferida ?? 'N/A'}</Typography>
                          <Typography variant="body2">Percentil: {reyFigurePercentiles.memoriaExactitud?.percentile ?? 'N/A'}</Typography>
                          {(reyFigurePercentiles.memoriaExactitud?.classifications?.length ?? 0) > 0 ? (
                            <>
                              <Typography variant="body2">Calificación:</Typography>
                              <Chip size="small" variant="filled" label={reyFigurePercentiles.memoriaExactitud!.classifications!.join(' / ')} sx={{ bgcolor: classificationColors[reyFigurePercentiles.memoriaExactitud!.classifications![0]] || '#757575', color: 'white', fontWeight: 'bold' }} />
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Calificación: —</Typography>
                          )}
                        </Box>
                      </Box>
                    </>
                  )}

                {/* Datos D2 */}
                {isD2Confirm && (
                  <>
                    <Box mb={3}>
                      <Typography variant="h6" gutterBottom>
                        Resultados del Test D2
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {[
                          { key: 'TR', label: 'TR', score: formData.testData?.trScore, pct: d2Percentiles.TR?.percentile, cls: d2Percentiles.TR?.classifications, conv: d2Conversions.TR || [] },
                          { key: 'TA', label: 'TA', score: formData.testData?.taScore, pct: d2Percentiles.TA?.percentile, cls: d2Percentiles.TA?.classifications, conv: d2Conversions.TA || [] },
                          { key: 'O', label: 'O', score: formData.testData?.oScore, pct: d2Percentiles.O?.percentile, cls: d2Percentiles.O?.classifications, conv: d2Conversions.O || [] },
                          { key: 'C', label: 'C', score: formData.testData?.cScore, pct: d2Percentiles.C?.percentile, cls: d2Percentiles.C?.classifications, conv: d2Conversions.C || [] },
                          { key: 'TOT', label: 'TOT', score: formData.testData?.totScore, pct: d2Percentiles.TOT?.percentile, cls: d2Percentiles.TOT?.classifications, conv: d2Conversions.TOT || [] },
                          { key: 'CON', label: 'Concentración', score: formData.testData?.concentrationScore, pct: d2Percentiles.CON?.percentile, cls: d2Percentiles.CON?.classifications, conv: d2Conversions.CON || [] },
                          { key: 'TR+', label: 'TR+', score: formData.testData?.trPlusScore, pct: d2Percentiles['TR+']?.percentile, cls: d2Percentiles['TR+']?.classifications, conv: d2Conversions['TR+'] || [] },
                          { key: 'TR-', label: 'TR-', score: formData.testData?.trMinusScore, pct: d2Percentiles['TR-']?.percentile, cls: d2Percentiles['TR-']?.classifications, conv: d2Conversions['TR-'] || [] },
                          { key: 'VAR', label: 'VAR', score: formData.testData?.varScore, pct: d2Percentiles.VAR?.percentile, cls: d2Percentiles.VAR?.classifications, conv: d2Conversions.VAR || [] }
                        ].map((metric) => (
                          <Box key={metric.key} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                            <Typography variant="subtitle2" fontWeight="bold">{metric.label}</Typography>
                            <Typography variant="body2">Puntaje: {metric.score ?? 'N/A'}</Typography>
                            <Typography variant="body2">Percentil: {metric.pct ?? 'N/A'}</Typography>
                            {(metric.cls?.length ?? 0) > 0 ? (
                              <Chip size="small" variant="filled" label={metric.cls!.join(' / ')} sx={{ mt: 0.5, bgcolor: classificationColors[metric.cls![0]] || '#757575', color: 'white', fontWeight: 'bold' }} />
                            ) : (
                              <Typography variant="caption" color="text.secondary">Calificación: —</Typography>
                            )}
                            <Box mt={1}>
                              {metric.conv.length > 0 ? metric.conv.slice(0, 2).map((conv: any, idx: number) => (
                                <Typography key={`${metric.key}-confirm-conv-${idx}`} variant="caption" display="block" color="text.secondary">
                                  Escalar: {conv?.scaledScore?.average ?? '—'} | T: {conv?.tScore?.average ?? '—'} | Z: {conv?.zScore?.average ?? '—'} | CI: {conv?.standardScore?.average ?? '—'}
                                </Typography>
                              )) : (
                                <Typography variant="caption" color="text.secondary">Conversión: —</Typography>
                              )}
                            </Box>
                          </Box>
                        ))}

                        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                          <Typography variant="subtitle2" fontWeight="bold">% Error</Typography>
                          <Typography variant="body2">{formData.testData?.errorRate ?? 'N/A'}</Typography>
                          <Typography variant="caption" color="text.secondary">100 * (O + C) / TR</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}

                {isElevator3BConfirm && (
                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                      Resultados Elevator 3B
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {([
                        { key: 'versionA' as ElevatorVersionKey, title: 'Versión A', score: calculateElevatorTotalScore('versionA'), max: 7 },
                        { key: 'versionB' as ElevatorVersionKey, title: 'Versión B', score: calculateElevatorTotalScore('versionB'), max: 10 },
                      ]).map((item) => (
                        <Box key={item.key} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                          <Typography variant="subtitle2" fontWeight="bold">{item.title}</Typography>
                          <Typography variant="body2">Aciertos: {item.score}/{item.max}</Typography>
                          <Typography variant="body2">Percentil: {elevatorPercentiles[item.key]?.percentile ?? 'N/A'}</Typography>
                          {(elevatorPercentiles[item.key]?.classifications?.length ?? 0) > 0 ? (
                            <Chip
                              size="small"
                              variant="filled"
                              label={elevatorPercentiles[item.key]!.classifications.join(' / ')}
                              sx={{ mt: 0.5, bgcolor: classificationColors[elevatorPercentiles[item.key]!.classifications[0]] || '#757575', color: 'white', fontWeight: 'bold' }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">Calificación: —</Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Datos BNT */}
                {isBNTConfirm && (
                  <>
                    <Box mb={3}>
                      <Typography variant="h6" gutterBottom>
                        Resultados del Boston Naming Test
                      </Typography>
                      
                      <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'success.50', border: '2px solid', borderColor: 'success.main', mb: 3 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                              Score Total (Respuestas Correctas)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Total: {formData.testData?.bntScore || calculateBntTotalScore()} / {BNT_WORD_LIST.length}
                            </Typography>
                          </Box>
                          <Chip
                            label={formData.testData?.bntScore || calculateBntTotalScore()}
                            color="success"
                            sx={{ fontSize: '1.5rem', fontWeight: 'bold', px: 3, py: 3 }}
                          />
                        </Box>
                        
                        {/* Mostrar percentil de BNT */}
                        {bntPercentile && bntPercentile.percentile && (
                          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                            <Chip
                              label={`Percentil: ${bntPercentile.percentile}`}
                              color="info"
                              size="medium"
                              variant="outlined"
                              sx={{ fontWeight: 'bold' }}
                            />
                            {bntPercentile.classifications && bntPercentile.classifications.length > 0 && (
                              bntPercentile.classifications.length > 1 ? (
                                <Chip
                                  label={`Clasificación: ${bntPercentile.classifications.join(' / ')}`}
                                  variant="filled"
                                  size="medium"
                                  sx={{
                                    background: `linear-gradient(90deg, ${classificationColors[bntPercentile.classifications[0]] || '#757575'} 0%, ${classificationColors[bntPercentile.classifications[1]] || '#757575'} 100%)`,
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                />
                              ) : (
                                <Chip
                                  label={`Clasificación: ${bntPercentile.classifications[0]}`}
                                  variant="filled"
                                  size="medium"
                                  sx={{
                                    bgcolor: classificationColors[bntPercentile.classifications[0]] || '#757575',
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                />
                              )
                            )}
                          </Box>
                        )}
                      </Card>

                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                        Resumen de Evaluación:
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" mt={1}>
                        <Chip 
                          label={`Respuestas Correctas: ${Object.values(bntResponses).filter(r => r.respuestaCorrecta).length}`}
                          color="success"
                          variant="outlined"
                        />
                        <Chip 
                          label={`Claves Semánticas: ${Object.values(bntResponses).filter(r => r.claveSemantica).length}`}
                          color="info"
                          variant="outlined"
                        />
                        <Chip 
                          label={`Claves Fonéticas: ${Object.values(bntResponses).filter(r => r.claveFonetica).length}`}
                          color="info"
                          variant="outlined"
                        />
                        <Chip 
                          label={`Parafasias Fonológicas: ${Object.values(bntResponses).filter(r => r.parafasiasFonologica).length}`}
                          color="warning"
                          variant="outlined"
                        />
                        <Chip 
                          label={`Parafasias Verbales: ${Object.values(bntResponses).filter(r => r.parafasiasVerbales).length}`}
                          color="warning"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </>
                )}

                {isMocaConfirm && (
                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                      Resultado de MoCA
                    </Typography>

                    <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'success.50', border: '2px solid', borderColor: 'success.main', mb: 3 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                            Puntaje Directo Total
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Total: {formData.testData?.mocaScore ?? '--'} / 30
                          </Typography>
                        </Box>
                        <Chip
                          label={formData.testData?.mocaScore ?? '--'}
                          color="success"
                          sx={{ fontSize: '1.5rem', fontWeight: 'bold', px: 3, py: 3 }}
                        />
                      </Box>

                      {mocaPercentile && mocaPercentile.percentile !== null && mocaPercentile.percentile !== undefined && (
                        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                          <Chip
                            label={`Percentil: ${mocaPercentile.percentile}`}
                            color="info"
                            size="medium"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                          {mocaPercentile.classifications && mocaPercentile.classifications.length > 0 && (
                            mocaPercentile.classifications.length > 1 ? (
                              <Chip
                                label={`Clasificación: ${mocaPercentile.classifications.join(' / ')}`}
                                variant="filled"
                                size="medium"
                                sx={{
                                  background: `linear-gradient(90deg, ${classificationColors[mocaPercentile.classifications[0]] || '#757575'} 0%, ${classificationColors[mocaPercentile.classifications[1]] || '#757575'} 100%)`,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            ) : (
                              <Chip
                                label={`Clasificación: ${mocaPercentile.classifications[0]}`}
                                variant="filled"
                                size="medium"
                                sx={{
                                  bgcolor: classificationColors[mocaPercentile.classifications[0]] || '#757575',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            )
                          )}
                        </Box>
                      )}
                      {mocaPercentile && mocaPercentile.educationAdjustment !== undefined && mocaPercentile.educationAdjustment !== null && mocaPercentile.educationAdjustment > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {`* Puntaje ajustado por escolaridad: ${mocaPercentile.rawScore} + ${mocaPercentile.educationAdjustment} = ${mocaPercentile.adjustedScore} / 30 (usado para el percentil)`}
                        </Typography>
                      )}
                    </Card>

                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Detalle por dominio — lo marcado y no marcado
                    </Typography>
                    <MocaDetailedView data={mocaDetailed} />
                  </Box>
                )}

                {isIFSChConfirm && (
                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                      Resultado de IFS-Ch
                    </Typography>

                    <Card sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.15)' : 'primary.50', border: '2px solid', borderColor: 'primary.main', mb: 2 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" color="primary">
                            Puntaje Total
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Total: {ifsChTotalScore ?? '--'} / 30
                            {ifsChWorkingMemoryIndex !== undefined && ` · Índice Memoria de Trabajo: ${ifsChWorkingMemoryIndex} / 10`}
                          </Typography>
                        </Box>
                        <Chip
                          label={ifsChTotalScore ?? '--'}
                          color="primary"
                          sx={{ fontSize: '1.5rem', fontWeight: 'bold', px: 3, py: 3 }}
                        />
                      </Box>

                      {ifsChPercentiles.total && ifsChPercentiles.total.percentile !== null && ifsChPercentiles.total.percentile !== undefined && (
                        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                          <Chip
                            label={`Z: ${ifsChPercentiles.total.zScore ?? 'N/A'} · Percentil: ${ifsChPercentiles.total.percentile}`}
                            color="info"
                            size="medium"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                          {ifsChPercentiles.total.classifications && ifsChPercentiles.total.classifications.length > 0 && (
                            ifsChPercentiles.total.classifications.length > 1 ? (
                              <Chip
                                label={`Clasificación: ${ifsChPercentiles.total.classifications.join(' / ')}`}
                                variant="filled"
                                size="medium"
                                sx={{
                                  background: `linear-gradient(90deg, ${classificationColors[ifsChPercentiles.total.classifications[0]] || '#757575'} 0%, ${classificationColors[ifsChPercentiles.total.classifications[1]] || '#757575'} 100%)`,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            ) : (
                              <Chip
                                label={`Clasificación: ${ifsChPercentiles.total.classifications[0]}`}
                                variant="filled"
                                size="medium"
                                sx={{
                                  bgcolor: classificationColors[ifsChPercentiles.total.classifications[0]] || '#757575',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                            )
                          )}
                        </Box>
                      )}
                      {renderMatrixComparisonBoxes(ifsChPercentiles.total?.matrices)}
                    </Card>

                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Detalle por subtest
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {TEST_CONFIGS['IFS-CH'].conditions.map((condition) => {
                        const score = formData.testData?.[`${condition.id}Score`];
                        const pct = ifsChPercentiles[condition.id];
                        return (
                          <Box key={condition.id} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Typography variant="body2">{condition.name}</Typography>
                              <Box display="flex" gap={1} alignItems="center">
                                <Chip label={`${score ?? '--'} / ${condition.max}`} size="small" variant="outlined" />
                                {pct && pct.percentile !== null && pct.percentile !== undefined && (
                                  <Chip label={`P${pct.percentile}`} size="small" color="info" />
                                )}
                              </Box>
                            </Box>
                            {renderMatrixComparisonBoxes(pct?.matrices)}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {formData.observations && (
                  <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      Observaciones
                    </Typography>
                    <Typography variant="body1">
                      {formData.observations}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        );
      }

      default:
        return null;
    }
  };

  // Determinar el ancho del modal según el tipo de prueba
  const isHVLTR = formData.testTypeCode === 'HVLT-R';
  const isReyFigure = formData.testTypeCode === 'REY-FIGURE';
  const isBNT = formData.testTypeCode === 'BNT';
  const isElevator3B = formData.testTypeCode === 'ELEVATOR-3B';
  const dialogMaxWidth = isReyFigure || isBNT || isElevator3B ? 'xl' : (isHVLTR ? 'lg' : 'md');

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth={dialogMaxWidth}
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <PsychologyIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Agregar Nueva Prueba
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancelar
        </Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack}>
            Anterior
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button 
            variant="contained" 
            onClick={handleNext}
            disabled={!formData.testTypeCode}
          >
            Siguiente
          </Button>
        ) : (
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={
              loading || 
              (formData.testTypeCode === 'STROOP'
                ? !(formData.testData?.palabraScore > 0 && formData.testData?.colorScore > 0 && formData.testData?.palabraColorScore > 0)
                : formData.testTypeCode === 'FLUENCIAS'
                ? !(formData.testData?.animalsScore > 0 && formData.testData?.letterFScore > 0 && formData.testData?.letterAScore > 0 && formData.testData?.letterSScore > 0)
                : formData.testTypeCode === 'HVLT-R'
                ? !(formData.testData?.ensayo1Score !== undefined && formData.testData?.ensayo2Score !== undefined && formData.testData?.ensayo3Score !== undefined && formData.testData?.ensayo4Score !== undefined)
                : formData.testTypeCode === 'REY-FIGURE'
                ? !(formData.testData?.copiaExactitud !== undefined && formData.testData?.copiaExactitud !== null && formData.testData?.copiaTiempo !== undefined && formData.testData?.copiaTiempo !== null && formData.testData?.memoriaExactitud !== undefined && formData.testData?.memoriaExactitud !== null)
                : formData.testTypeCode === 'BNT'
                ? !(formData.testData?.bntScore !== undefined && formData.testData.bntScore >= 0)
                : formData.testTypeCode === 'D2'
                ? false
                : formData.testTypeCode === 'ELEVATOR-3B'
                ? false
                : formData.testTypeCode === 'MOCA'
                ? !(formData.testData?.mocaScore !== undefined && formData.testData?.mocaScore !== null && formData.testData.mocaScore >= 0 && formData.testData.mocaScore <= 30)
                : formData.testTypeCode === 'IFS-CH'
                ? ifsChTotalScore === undefined
                : !formData.testData?.timeSeconds || formData.testData.timeSeconds <= 0
              )
            }
            startIcon={<SaveIcon />}
          >
            {loading ? 'Guardando...' : 'Guardar Prueba'}
          </Button>
        )}
      </DialogActions>

      {/* Modal para preview de imagen */}
      <Dialog
        open={imagePreviewModal.open}
        onClose={() => setImagePreviewModal({ open: false, imageUrl: null, title: '' })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{imagePreviewModal.title}</Typography>
            <IconButton
              onClick={() => setImagePreviewModal({ open: false, imageUrl: null, title: '' })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {imagePreviewModal.imageUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
              <img
                src={imagePreviewModal.imageUrl}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  borderRadius: '8px',
                  border: '1px solid #ddd'
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagePreviewModal({ open: false, imageUrl: null, title: '' })}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AddTestModal;
